import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { CompetencyLevel, CompetencyDotsDisplay } from '../components/CompetencyLevel';
import { RichTextEditor } from '../components/RichTextEditor';
import { ImportDialog, ManualImportDialog, BulkCompetenciesDialog, PreviewDialog, TranslateCVDialog, ExportLangDialog } from '../components/cv';
import { 
  User, Briefcase, GraduationCap, Languages, Award, 
  Plus, Trash2, Download, Save, FileText, CheckCircle, Globe,
  Eye, Upload, Palette, Settings2, Sparkles, Import, ListPlus,
  PenLine, List, ArrowUp, ArrowDown, ArrowUpFromLine
} from 'lucide-react';
import { toast } from 'sonner';
import { Search as SearchIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Textarea that auto-grows to fit its content. Replaces the fixed-height
// Textarea so the user can read the full Professional Summary at a glance.
const AutoGrowTextarea = ({ value, onChange, placeholder, className = '', ...rest }) => {
  const ref = React.useRef(null);
  const resize = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  React.useEffect(() => { resize(); }, [value, resize]);
  return (
    <textarea
      ref={ref}
      value={value || ''}
      onChange={(e) => { onChange?.(e); resize(); }}
      placeholder={placeholder}
      rows={3}
      className={`flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none overflow-hidden min-h-[80px] ${className}`}
      {...rest}
    />
  );
};

// Competency Search Input with autocomplete + create new
const CompetencySearchInput = ({ value, competencies, language, onSelect, onRequestNew, placeholder }) => {
  const [search, setSearch] = React.useState(value || '');
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => { setSearch(value || ''); }, [value]);

  const filtered = competencies.filter(c => {
    const name = language === 'pt' ? c.name_pt : c.name_en;
    return name?.toLowerCase().includes(search.toLowerCase());
  }).slice(0, 10);

  const exactMatch = competencies.some(c =>
    (c.name_pt?.toLowerCase() === search.toLowerCase()) ||
    (c.name_en?.toLowerCase() === search.toLowerCase())
  );

  // Persist free-text values as custom competency entries so they survive
  // tab-out / save without requiring the user to click a dropdown item.
  const persistFreeText = () => {
    const txt = (search || '').trim();
    if (!txt) return;
    if (txt === (value || '').trim()) return; // unchanged
    onSelect({ id: '', name_pt: txt, name_en: txt });
  };

  return (
    <div className="relative">
      <div className="flex items-center border rounded-md bg-white">
        <SearchIcon className="w-4 h-4 ml-2 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Delay so click on dropdown items wins over blur
            setTimeout(() => {
              setOpen(false);
              persistFreeText();
            }, 200);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              persistFreeText();
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
          placeholder={placeholder}
          className="flex-1 px-2 py-2 text-sm outline-none bg-transparent"
          data-testid="competency-search-input"
        />
      </div>
      {open && search.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {filtered.map(c => (
            <button
              key={c.id}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#f0fdfa] transition-colors"
              onMouseDown={() => { onSelect(c); setSearch(language === 'pt' ? c.name_pt : c.name_en); setOpen(false); }}
            >
              {language === 'pt' ? c.name_pt : c.name_en}
              <span className="text-xs text-gray-400 ml-2">{c.category}</span>
            </button>
          ))}
          {!exactMatch && search.trim().length > 1 && (
            <button
              className="w-full text-left px-3 py-2 text-sm text-[#1E3C3C] font-medium border-t hover:bg-[#f0fdfa] transition-colors"
              onMouseDown={() => { onRequestNew(search.trim()); setOpen(false); }}
              data-testid="request-new-competency-btn"
            >
              + {language === 'pt' ? `Solicitar nova competência: "${search.trim()}"` : `Request new competency: "${search.trim()}"`}
            </button>
          )}
          {filtered.length === 0 && exactMatch && (
            <div className="px-3 py-2 text-sm text-gray-400">
              {language === 'pt' ? 'Nenhum resultado' : 'No results'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Expanded predefined languages
const AVAILABLE_LANGUAGES = [
  { id: 'portuguese', name_pt: 'Português', name_en: 'Portuguese' },
  { id: 'english', name_pt: 'Inglês', name_en: 'English' },
  { id: 'french', name_pt: 'Francês', name_en: 'French' },
  { id: 'spanish', name_pt: 'Espanhol', name_en: 'Spanish' },
  { id: 'italian', name_pt: 'Italiano', name_en: 'Italian' },
  { id: 'german', name_pt: 'Alemão', name_en: 'German' },
  { id: 'mandarin', name_pt: 'Mandarim', name_en: 'Mandarin' },
];

const LANGUAGE_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const FONT_OPTIONS = [
  'IBM Plex Sans', 'Arial', 'Calibri', 'Times New Roman', 'Georgia', 'Verdana', 'Helvetica', 'Roboto', 'Open Sans'
];

// Language Selection Component
const LanguageSelector = ({ onSelect }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f0fdfa] to-white px-4" data-testid="language-selector">
      <div className="w-full max-w-lg text-center">
        <div className="w-16 h-16 rounded-xl bg-[#1E3C3C] flex items-center justify-center mx-auto mb-6">
          <Globe className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          Escolha o Formato / Choose Format
        </h1>
        <p className="text-[#64748b] mb-8">
          Selecione o idioma do seu CV / Select your CV language
        </p>
        <div className="grid grid-cols-2 gap-4">
          <Button onClick={() => onSelect('pt')} className="py-8 text-lg bg-[#1E3C3C] hover:bg-[#0F766E] text-white" data-testid="select-pt-btn">
            🇵🇹 Português
          </Button>
          <Button onClick={() => onSelect('en')} className="py-8 text-lg bg-[#FFD700] hover:bg-[#E6C200] text-[#1a202c]" data-testid="select-en-btn">
            🇬🇧 English
          </Button>
        </div>
      </div>
    </div>
  );
};

export const CVBuilder = () => {
  const { token } = useAuth();
  const { t, language, chooseLanguage } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const cvId = searchParams.get('id');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [competencies, setCompetencies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [previewing, setPreviewing] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [summaryStyle, setSummaryStyle] = useState('direto'); // direto | impacto | conciso | narrativo | senior
  const [summaryVariations, setSummaryVariations] = useState([]); // list of alternative wordings
  const [showSummaryPicker, setShowSummaryPicker] = useState(false);
  // Track which experience index is currently generating bullets/technologies via AI.
  const [suggestingBulletsIdx, setSuggestingBulletsIdx] = useState(null);
  const [suggestingTechIdx, setSuggestingTechIdx] = useState(null);
  // Same idea for nested project cards — track which project is generating.
  // Value is a "expIdx-projIdx" string so only one card shows the spinner.
  const [suggestingProjectBulletsKey, setSuggestingProjectBulletsKey] = useState(null);
  const [suggestingProjectTechKey, setSuggestingProjectTechKey] = useState(null);
  const [fixingLanguage, setFixingLanguage] = useState(false);
  // Auto-save state (Feb 2026 v2 — user spec: edits must persist without clicking "Guardar").
  const autoSaveTimer = useRef(null);
  const autoSaveInFlight = useRef(false);
  const skipNextAutoSave = useRef(true);  // skip on initial mount + on load-from-DB
  const isDirty = useRef(false);           // tracks whether there are unsaved edits since last successful save
  const latestCVRef = useRef(null);        // always points to the freshest cv, used by beforeunload/blur flushes
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showManualImportDialog, setShowManualImportDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showBulkCompDialog, setShowBulkCompDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [manualImportText, setManualImportText] = useState('');
  const [manualImporting, setManualImporting] = useState(false);
  const [selectedBulkComps, setSelectedBulkComps] = useState([]);
  const [itFunctions, setItFunctions] = useState([]);
  // Translation prompt state
  const [showTranslateDialog, setShowTranslateDialog] = useState(false);
  const [translateLangs, setTranslateLangs] = useState([]);
  const [translating, setTranslating] = useState(false);
  // Export Word language prompt state
  const [showExportLangDialog, setShowExportLangDialog] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [cv, setCV] = useState({
    template: 'outsourcing',
    name: '',
    function_title: '',
    client_id: '',
    client_name: '',
    technical_test_result: null,
    technical_test_observations: '',
    interview_score: null,
    interview_observations: '',
    evaluation_summary: '',
    score_evolutt: null,
    career_start_year: new Date().getFullYear() - 5,
    career_start_month: 1,
    years_experience: 5,
    months_experience: 0,
    experience_override: '', // Manual override for experience display
    availability_pt: '',
    availability_en: '',
    main_competencies: [],
    summary_pt: '',
    summary_en: '',
    experiences: [],
    education: [],
    certifications: [],
    languages: [],
    cv_language: language || 'pt',
    cv_type: 'tech',  // 'tech' = full CV (with Professional Experience), 'rl' = Recém-Licenciado (academic focus, no experience)
    rl_projects: '',  // Only used when cv_type === 'rl' — freeform academic/personal projects text.
    header_config: {
      logo_base64: null,
      primary_color: '#1E3C3C',
      secondary_color: '#FFD700',
      font_family: 'IBM Plex Sans',
      company_name: ''
    }
  });

  const [clients, setClients] = useState([]);
  const [availabilityOptions, setAvailabilityOptions] = useState([]);
  const [generatingObservation, setGeneratingObservation] = useState(null);

  // Calculate years and months of experience automatically
  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 0-indexed
    
    const startYear = cv.career_start_year || currentYear;
    const startMonth = cv.career_start_month || 1;
    
    // Calculate total months
    const totalMonths = (currentYear - startYear) * 12 + (currentMonth - startMonth);
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    
    setCV(prev => ({ 
      ...prev, 
      years_experience: Math.max(0, years),
      months_experience: Math.max(0, months)
    }));
  }, [cv.career_start_year, cv.career_start_month]);

  useEffect(() => {
    if (language) {
      setCV(prev => ({ ...prev, cv_language: language }));
    }
  }, [language]);

  // Auto-populate main competencies from technologies in experiences
  // ONLY runs when main_competencies is empty (e.g. right after AI import).
  // This prevents the user's manual edits from being overwritten whenever
  // they touch the experiences section. See user feedback Feb 2026.
  useEffect(() => {
    if (cv.main_competencies && cv.main_competencies.length > 0) return;

    const techMaxLevel = {};

    for (const exp of cv.experiences) {
      if (exp.technologies && exp.technologies.length > 0) {
        for (const tech of exp.technologies) {
          if (tech.name && tech.name.trim()) {
            const name = tech.name.trim();
            const level = parseInt(tech.level) || 3;
            techMaxLevel[name] = Math.max(techMaxLevel[name] || 0, level);
          }
        }
      }
      if (exp.technologies_text && exp.technologies_text.trim()) {
        const lines = exp.technologies_text.split('\n').map(l => l.trim()).filter(Boolean);
        for (const line of lines) {
          techMaxLevel[line] = Math.max(techMaxLevel[line] || 0, 3);
        }
      }
    }
    
    const entries = Object.entries(techMaxLevel);
    if (entries.length === 0) return;
    
    // Sort by level descending, take top 7
    entries.sort((a, b) => b[1] - a[1]);
    const top7 = entries.slice(0, 7);
    
    const autoComps = top7.map(([name, level]) => {
      const match = competencies.find(c => 
        c.name_pt?.toLowerCase() === name.toLowerCase() || 
        c.name_en?.toLowerCase() === name.toLowerCase() ||
        c.keywords?.some(k => k.toLowerCase() === name.toLowerCase())
      );
      
      return {
        competency_id: match ? match.id : '',
        name_pt: match ? match.name_pt : name,
        name_en: match ? match.name_en : name,
        level: Math.max(1, Math.min(5, level))
      };
    });
    
    setCV(prev => ({ ...prev, main_competencies: autoComps }));
  }, [cv.experiences, cv.main_competencies, competencies]);

  useEffect(() => {
    fetchCompetencies();
    fetchCategories();
    fetchClients();
    fetchAvailabilityOptions();
    fetchItFunctions();
    if (cvId) fetchCV();
  }, [cvId]);

  // Auto-save: after 1.5 seconds of no changes, persist the CV silently.
  // Skip on initial mount and right after loading from the DB (skipNextAutoSave).
  useEffect(() => {
    // Keep the freshest cv snapshot around for the beforeunload/pagehide flush
    // below — it uses a ref so the handler doesn't need to re-bind on every state change.
    latestCVRef.current = cv;
    if (skipNextAutoSave.current) {
      skipNextAutoSave.current = false;
      return;
    }
    isDirty.current = true;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      if (autoSaveInFlight.current) return;
      if (!cvId) return;  // brand-new CV: user must click Guardar first
      autoSaveInFlight.current = true;
      setAutoSaveStatus('saving');
      try {
        await axios.put(`${API}/cvs/${cvId}`, cv, {
          headers: { Authorization: `Bearer ${token}` },
        });
        isDirty.current = false;
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus((s) => (s === 'saved' ? 'idle' : s)), 5000);
      } catch (e) {
        setAutoSaveStatus('error');
        console.warn('Auto-save failed:', e?.response?.status || e?.message);
      } finally {
        autoSaveInFlight.current = false;
      }
    }, 1500);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cv]);

  // Belt-and-braces: on tab close / browser navigation, if there are unsaved
  // edits, (a) warn the user with the browser confirmation dialog AND (b) fire
  // a best-effort synchronous PUT via `navigator.sendBeacon` so the server
  // still receives the changes even when the tab is closing.
  useEffect(() => {
    const flushOnUnload = (e) => {
      if (!isDirty.current || !cvId) return;
      try {
        const backendBase = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
        const payload = new Blob(
          [JSON.stringify(latestCVRef.current || {})],
          { type: 'application/json' }
        );
        // sendBeacon can't set an Authorization header, so we fall back to
        // synchronous XHR (kept alive by the browser during unload). This is
        // the last-mile safety net; the debounced auto-save handles 99% of cases.
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', `${backendBase}/cvs/${cvId}`, false);  // sync
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        try { xhr.send(payload); } catch (_) {}
      } catch (_) {}
      // Also show the standard "You have unsaved changes" prompt.
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', flushOnUnload);
    return () => window.removeEventListener('beforeunload', flushOnUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cvId, token]);

  const fetchCompetencies = async () => {
    try {
      const response = await axios.get(`${API}/competencies`);
      setCompetencies(response.data);
    } catch (error) {
      console.error('Error fetching competencies:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/competencies/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchAvailabilityOptions = async () => {
    try {
      const response = await axios.get(`${API}/availability-options`);
      setAvailabilityOptions(response.data);
    } catch (error) {
      console.error('Error fetching availability options:', error);
    }
  };

  const fetchItFunctions = async () => {
    try {
      const response = await axios.get(`${API}/functions`);
      setItFunctions(response.data);
    } catch (error) {
      console.error('Error fetching IT functions:', error);
    }
  };

  const fetchCV = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cvs/${cvId}`, { headers: { Authorization: `Bearer ${token}` } });
      setCV(response.data);
      skipNextAutoSave.current = true;  // avoid immediately auto-saving what we just loaded
      if (response.data.cv_language) chooseLanguage(response.data.cv_language);
    } catch (error) {
      toast.error(t('error'));
      navigate('/cvs');
    } finally {
      setLoading(false);
    }
  };

  // Fix any English / Brazilian PT content in the CV to European Portuguese.
  // Used to backfill CVs imported before the Feb 2026 language-lock fix.
  // Never destructive: content already in PT-PT is preserved verbatim by the LLM.
  const handleFixLanguage = async () => {
    const msg = language === 'pt'
      ? 'A IA vai percorrer todas as experiências e traduzir para PT-PT o que estiver em inglês ou português do Brasil. Conteúdo já em PT-PT fica intacto. Continuar?'
      : 'AI will scan every experience and translate any English or Brazilian PT to European PT. Continue?';
    if (!window.confirm(msg)) return;
    // Snapshot for undo
    const snapshot = JSON.parse(JSON.stringify(cv));
    setFixingLanguage(true);
    try {
      const response = await axios.post(`${API}/ai/fix-language`, {
        summary_pt: cv.summary_pt || '',
        experiences: cv.experiences || [],
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });
      setCV({
        ...cv,
        summary_pt: response.data?.summary_pt ?? cv.summary_pt,
        experiences: Array.isArray(response.data?.experiences) ? response.data.experiences : cv.experiences,
        // Clear summary_en so translate_cv() regenerates from the freshly PT-PT source
        summary_en: '',
      });
      toast.success(language === 'pt' ? 'CV corrigido para PT-PT' : 'CV fixed to European PT', {
        duration: 6000,
        action: {
          label: language === 'pt' ? 'Anular' : 'Undo',
          onClick: () => { setCV(snapshot); toast.success(language === 'pt' ? 'CV restaurado' : 'CV restored'); },
        },
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (language === 'pt' ? 'Erro ao corrigir idioma' : 'Error fixing language'));
    } finally {
      setFixingLanguage(false);
    }
  };


  const handleClearCV = () => {
    const msg = language === 'pt'
      ? 'Tem a certeza que quer limpar TODOS os dados deste CV?'
      : 'Are you sure you want to clear ALL data in this CV?';    if (!window.confirm(msg)) return;
    // Snapshot the current CV so the user can undo within 5s.
    // Deep clone via JSON to avoid nested-object aliasing.
    const snapshot = JSON.parse(JSON.stringify(cv));
    // Reset to the same shape as the initial useState default so no stale
    // key survives (avoids "some fields cleared, others not" bugs).
    setCV({
      template: 'outsourcing',
      name: '',
      function_title: '',
      client_id: '',
      client_name: '',
      technical_test_result: null,
      technical_test_observations: '',
      interview_score: null,
      interview_observations: '',
      evaluation_summary: '',
      score_evolutt: null,
      career_start_year: new Date().getFullYear() - 5,
      career_start_month: 1,
      years_experience: 5,
      months_experience: 0,
      experience_override: '',
      availability_pt: '',
      availability_en: '',
      main_competencies: [],
      summary_pt: '',
      summary_en: '',
      experiences: [],
      education: [],
      certifications: [],
      languages: [],
      cv_language: language || 'pt',
      cv_type: 'tech',
      rl_projects: '',
      header_config: {
        logo_base64: null,
        primary_color: '#1E3C3C',
        secondary_color: '#FFD700',
        font_family: 'IBM Plex Sans',
        company_name: ''
      }
    });
    // Show a 5s toast with an Undo action so accidental clears are recoverable.
    toast.success(language === 'pt' ? 'CV limpo' : 'CV cleared', {
      duration: 5000,
      action: {
        label: language === 'pt' ? 'Anular' : 'Undo',
        onClick: () => {
          setCV(snapshot);
          toast.success(language === 'pt' ? 'CV restaurado' : 'CV restored');
        },
      },
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cvData = { ...cv, cv_language: language };
      let savedId = cvId;
      const isNew = !cvId;
      if (cvId) {
        await axios.put(`${API}/cvs/${cvId}`, cvData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        const response = await axios.post(`${API}/cvs`, cvData, { headers: { Authorization: `Bearer ${token}` } });
        savedId = response.data.id;
        navigate(`/cv/new?id=${savedId}`);
      }
      toast.success(t('saveSuccess'));
      void isNew;
    } catch (error) {
      // Surface a specific, actionable error message so production failures
      // are diagnosable (instead of the previous generic "Ocorreu um erro").
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message;
      let msg;
      if (status === 401 || status === 403) {
        msg = language === 'pt'
          ? 'Sessão expirou. Saia e entre novamente.'
          : 'Session expired. Please sign in again.';
      } else if (status === 413) {
        msg = language === 'pt'
          ? 'CV demasiado grande. Remova algum conteúdo e tente outra vez.'
          : 'CV too large. Remove some content and try again.';
      } else if (status === 422) {
        msg = language === 'pt'
          ? `Dados inválidos: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`
          : `Invalid data: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`;
      } else if (status && status >= 500) {
        msg = language === 'pt'
          ? `Erro do servidor (HTTP ${status}). ${detail || 'Tente novamente em alguns segundos.'}`
          : `Server error (HTTP ${status}). ${detail || 'Try again in a few seconds.'}`;
      } else if (!status) {
        msg = language === 'pt'
          ? 'Sem ligação ao servidor. Verifique a sua internet.'
          : 'No connection to server. Check your internet.';
      } else {
        msg = `${detail || t('error')} (HTTP ${status})`;
      }
      console.error('CV save failed:', { status, detail, error });
      toast.error(msg, { duration: 12000 });
    } finally {
      setSaving(false);
      isDirty.current = false;  // manual save clears the dirty flag → no beforeunload warning
    }
  };

  const handleTranslate = async () => {
    if (!cvId && !translateLangs.length) return;
    const targetId = cvId; // After save, cvId is set via URL navigation.
    if (!targetId) {
      toast.error(language === 'pt' ? 'Guarde o CV primeiro' : 'Save CV first');
      return;
    }
    if (translateLangs.length === 0) {
      setShowTranslateDialog(false);
      return;
    }
    setTranslating(true);
    toast.info(language === 'pt'
      ? `A traduzir para ${translateLangs.length} idioma(s)... pode demorar 1-2 minutos por idioma`
      : `Translating to ${translateLangs.length} language(s)... may take 1-2 minutes per language`);
    try {
      const resp = await axios.post(
        `${API}/cvs/${targetId}/translate`,
        { target_languages: translateLangs },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 360000 }
      );
      const created = resp.data?.created || [];
      const failed = resp.data?.failed || [];
      if (created.length) {
        const codes = created.map(c => c.lang.toUpperCase()).join(', ');
        toast.success(language === 'pt'
          ? `Versões criadas: ${codes}`
          : `Versions created: ${codes}`);
      }
      if (failed.length) {
        const codes = failed.map(f => f.lang.toUpperCase()).join(', ');
        toast.error(language === 'pt'
          ? `Falha na tradução para: ${codes}`
          : `Translation failed for: ${codes}`);
      }
      if (created.length === 0 && failed.length === 0) {
        toast.info(language === 'pt' ? 'Nada a traduzir' : 'Nothing to translate');
      }
      setShowTranslateDialog(false);
      setTranslateLangs([]);
    } catch (error) {
      const msg = error.response?.data?.detail
        || (language === 'pt' ? 'Erro ao traduzir o CV' : 'Error translating CV');
      toast.error(typeof msg === 'string' ? msg : (language === 'pt' ? 'Erro ao traduzir' : 'Translation error'));
    } finally {
      setTranslating(false);
    }
  };

  const handleGenerateScoreObservation = async (scoreType) => {
    const score = scoreType === 'interview' ? cv.interview_score : cv.technical_test_result;
    if (score === null || score === undefined) {
      toast.error(language === 'pt' ? 'Insira primeiro o score' : 'Enter score first');
      return;
    }
    
    setGeneratingObservation(scoreType);
    try {
      const response = await axios.post(`${API}/ai/score-observation`, {
        score: score,
        score_type: scoreType,
        lang: language
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (scoreType === 'interview') {
        setCV({ ...cv, interview_observations: response.data.observation });
      } else {
        setCV({ ...cv, technical_test_observations: response.data.observation });
      }
      toast.success(language === 'pt' ? 'Sugestão gerada!' : 'Suggestion generated!');
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setGeneratingObservation(null);
    }
  };

  const handleGenerateSummary = async () => {
    if (cv.experiences.length === 0 && cv.main_competencies.length === 0) {
      toast.error(language === 'pt' ? 'Adicione experiência ou competências primeiro' : 'Add experience or competencies first');
      return;
    }
    setGeneratingSummary(true);
    setSummaryVariations([]);
    try {
      const response = await axios.post(`${API}/ai/generate-summary`, {
        experiences: cv.experiences,
        competencies: cv.main_competencies,
        education: cv.education,
        language: language,
        style: summaryStyle,
        variations: 3,
      }, { headers: { Authorization: `Bearer ${token}` } });

      const list = Array.isArray(response.data?.summaries) && response.data.summaries.length > 0
        ? response.data.summaries
        : (response.data?.summary ? [response.data.summary] : []);
      if (list.length === 0) {
        toast.error(language === 'pt' ? 'A IA não devolveu nenhum resumo. Tente novamente.' : 'AI returned no summary. Try again.');
        return;
      }
      setSummaryVariations(list);
      setShowSummaryPicker(true);
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'pt' ? 'Erro ao gerar resumo' : 'Error generating summary');
      toast.error(typeof msg === 'string' ? msg : (language === 'pt' ? 'Erro ao gerar resumo' : 'Error generating summary'));
    } finally {
      setGeneratingSummary(false);
    }
  };

  const applySummaryVariation = (text) => {
    setCV({
      ...cv,
      summary_pt: language === 'pt' ? text : cv.summary_pt,
      // When editing in PT, clear summary_en so the export re-translates from
      // the freshly written PT source instead of showing a stale English one.
      summary_en: language === 'pt' ? '' : text,
    });
    setShowSummaryPicker(false);
    toast.success(language === 'pt' ? 'Resumo aplicado' : 'Summary applied');
  };

  const handleSuggestBullets = async (expIndex) => {
    const exp = cv.experiences[expIndex];
    if (!exp) return;
    const bulletField = language === 'pt' ? 'description_bullets_pt' : 'description_bullets_en';
    const existing = (exp[bulletField] || []).map(b => String(b).replace(/^[•\-*]\s*/, '').trim()).filter(Boolean);

    setSuggestingBulletsIdx(expIndex);
    try {
      const response = await axios.post(`${API}/ai/suggest-bullets`, {
        company: exp.company || '',
        position_pt: exp.position_pt || '',
        position_en: exp.position_en || '',
        existing_bullets: existing,
        count: 5,
        lang: language,
        is_current: !!exp.current,
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 45000,
      });
      const suggestions = response.data?.bullets || [];
      if (!suggestions.length) {
        toast.info(language === 'pt' ? 'A IA não encontrou novas responsabilidades a sugerir.' : 'AI found no new responsibilities to suggest.');
        return;
      }
      const updated = [...cv.experiences];
      const current = updated[expIndex][bulletField] || [];
      const newBullets = suggestions.map(s => `• ${s}`);
      updated[expIndex] = { ...updated[expIndex], [bulletField]: [...current, ...newBullets] };
      setCV({ ...cv, experiences: updated });
      toast.success(language === 'pt' ? `${suggestions.length} responsabilidades adicionadas` : `${suggestions.length} responsibilities added`);
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'pt' ? 'Erro ao sugerir responsabilidades' : 'Error suggesting responsibilities');
      toast.error(typeof msg === 'string' ? msg : (language === 'pt' ? 'Erro ao sugerir responsabilidades' : 'Error suggesting responsibilities'));
    } finally {
      setSuggestingBulletsIdx(null);
    }
  };

  const handleSuggestTechnologies = async (expIndex) => {
    const exp = cv.experiences[expIndex];
    if (!exp) return;
    const bulletField = language === 'pt' ? 'description_bullets_pt' : 'description_bullets_en';
    const bullets = (exp[bulletField] || []).map(b => String(b).replace(/^[•\-*]\s*/, '').trim()).filter(Boolean);
    const existingTech = (exp.technologies || []).filter(t => t && (t.name || '').trim());

    setSuggestingTechIdx(expIndex);
    try {
      const response = await axios.post(`${API}/ai/suggest-technologies`, {
        company: exp.company || '',
        position_pt: exp.position_pt || '',
        position_en: exp.position_en || '',
        bullets,
        existing_tech: existingTech,
        count: 6,
        lang: language,
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 40000,
      });
      const suggestions = response.data?.technologies || [];
      if (!suggestions.length) {
        toast.info(language === 'pt' ? 'A IA não encontrou novas tecnologias a sugerir.' : 'AI found no new technologies to suggest.');
        return;
      }
      const updated = [...cv.experiences];
      const current = updated[expIndex].technologies || [];
      updated[expIndex] = { ...updated[expIndex], technologies: [...current, ...suggestions] };
      setCV({ ...cv, experiences: updated });
      toast.success(language === 'pt' ? `${suggestions.length} tecnologias adicionadas` : `${suggestions.length} technologies added`);
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'pt' ? 'Erro ao sugerir tecnologias' : 'Error suggesting technologies');
      toast.error(typeof msg === 'string' ? msg : (language === 'pt' ? 'Erro ao sugerir tecnologias' : 'Error suggesting technologies'));
    } finally {
      setSuggestingTechIdx(null);
    }
  };

  // ---- AI helpers for NESTED PROJECTS ----
  // Reuse the same /ai/suggest-bullets and /ai/suggest-technologies endpoints,
  // but scope the context to the project (client + project name as "position",
  // parent experience company as "company", project's own bullets/tech).
  const handleSuggestProjectBullets = async (expIndex, projIndex) => {
    const exp = cv.experiences[expIndex];
    const proj = exp?.projects?.[projIndex];
    if (!proj) return;
    const bulletField = language === 'pt' ? 'description_bullets_pt' : 'description_bullets_en';
    const existing = (proj[bulletField] || []).map((b) => String(b).replace(/^[•\-*]\s*/, '').trim()).filter(Boolean);
    const projLabelPt = [proj.client, proj.name_pt].filter(Boolean).join(' — ');
    const projLabelEn = [proj.client, proj.name_en || proj.name_pt].filter(Boolean).join(' — ');

    const key = `${expIndex}-${projIndex}`;
    setSuggestingProjectBulletsKey(key);
    try {
      const response = await axios.post(`${API}/ai/suggest-bullets`, {
        company: exp.company || '',
        position_pt: projLabelPt || exp.position_pt || '',
        position_en: projLabelEn || exp.position_en || '',
        existing_bullets: existing,
        count: 5,
        lang: language,
        // A project is "current" when itself has current=true; otherwise
        // it inherits from its parent experience only if the project has
        // no explicit end_date set (i.e., ongoing under an ongoing role).
        is_current: !!proj.current || (!proj.end_date && !!exp.current),
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 45000,
      });
      const suggestions = response.data?.bullets || [];
      if (!suggestions.length) {
        toast.info(language === 'pt' ? 'A IA não encontrou novas responsabilidades a sugerir.' : 'AI found no new responsibilities to suggest.');
        return;
      }
      const updated = [...cv.experiences];
      const currentBullets = updated[expIndex].projects[projIndex][bulletField] || [];
      const newBullets = suggestions.map((s) => `• ${s}`);
      updated[expIndex].projects[projIndex] = {
        ...updated[expIndex].projects[projIndex],
        [bulletField]: [...currentBullets, ...newBullets],
      };
      setCV({ ...cv, experiences: updated });
      toast.success(language === 'pt' ? `${suggestions.length} responsabilidades adicionadas` : `${suggestions.length} responsibilities added`);
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'pt' ? 'Erro ao sugerir responsabilidades' : 'Error suggesting responsibilities');
      toast.error(typeof msg === 'string' ? msg : (language === 'pt' ? 'Erro ao sugerir responsabilidades' : 'Error suggesting responsibilities'));
    } finally {
      setSuggestingProjectBulletsKey(null);
    }
  };

  const handleSuggestProjectTechnologies = async (expIndex, projIndex) => {
    const exp = cv.experiences[expIndex];
    const proj = exp?.projects?.[projIndex];
    if (!proj) return;
    const bulletField = language === 'pt' ? 'description_bullets_pt' : 'description_bullets_en';
    const bullets = (proj[bulletField] || []).map((b) => String(b).replace(/^[•\-*]\s*/, '').trim()).filter(Boolean);
    const existingTech = (proj.technologies || []).filter((t) => t && (t.name || '').trim());
    const projLabelPt = [proj.client, proj.name_pt].filter(Boolean).join(' — ');
    const projLabelEn = [proj.client, proj.name_en || proj.name_pt].filter(Boolean).join(' — ');

    const key = `${expIndex}-${projIndex}`;
    setSuggestingProjectTechKey(key);
    try {
      const response = await axios.post(`${API}/ai/suggest-technologies`, {
        company: exp.company || '',
        position_pt: projLabelPt || exp.position_pt || '',
        position_en: projLabelEn || exp.position_en || '',
        bullets,
        existing_tech: existingTech,
        count: 6,
        lang: language,
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 40000,
      });
      const suggestions = response.data?.technologies || [];
      if (!suggestions.length) {
        toast.info(language === 'pt' ? 'A IA não encontrou novas tecnologias a sugerir.' : 'AI found no new technologies to suggest.');
        return;
      }
      const updated = [...cv.experiences];
      const current = updated[expIndex].projects[projIndex].technologies || [];
      updated[expIndex].projects[projIndex] = {
        ...updated[expIndex].projects[projIndex],
        technologies: [...current, ...suggestions],
      };
      setCV({ ...cv, experiences: updated });
      toast.success(language === 'pt' ? `${suggestions.length} tecnologias adicionadas` : `${suggestions.length} technologies added`);
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'pt' ? 'Erro ao sugerir tecnologias' : 'Error suggesting technologies');
      toast.error(typeof msg === 'string' ? msg : (language === 'pt' ? 'Erro ao sugerir tecnologias' : 'Error suggesting technologies'));
    } finally {
      setSuggestingProjectTechKey(null);
    }
  };

  // Helper to normalize imported CV data for state
  const normalizeImportedData = (data) => {
    const normalizeExp = (exp) => {
      const techs = Array.isArray(exp.technologies) 
        ? exp.technologies.map(t => typeof t === 'string' 
            ? { name: t, level: 3 } 
            : { name: t.name || '', level: Math.max(1, Math.min(5, parseInt(t.level) || 3)) })
        : [];
      // Generate technologies_text from imported technologies (one per line)
      const techText = techs.map(t => t.name).filter(Boolean).join('\n');
      // Preserve nested projects (consulting pattern). Structure is enforced
      // by the backend `_validate_cv_data`; here we just pass them through
      // so the CV builder can render them in the UI.
      const projects = Array.isArray(exp.projects) ? exp.projects.map((p) => ({
        kind: (p.kind === 'function' ? 'function' : 'project'),
        client: p.client || '',
        name_pt: p.name_pt || '',
        name_en: p.name_en || p.name_pt || '',
        start_date: p.start_date || '',
        end_date: p.end_date || '',
        current: !!p.current,
        description_pt: p.description_pt || '',
        description_en: p.description_en || '',
        description_bullets_pt: Array.isArray(p.description_bullets_pt) ? p.description_bullets_pt : [],
        description_bullets_en: Array.isArray(p.description_bullets_en) ? p.description_bullets_en : [],
        technologies: Array.isArray(p.technologies) ? p.technologies : [],
        technologies_text: Array.isArray(p.technologies) ? p.technologies.map(t => t.name).filter(Boolean).join('\n') : '',
      })) : [];
      return {
        company: exp.company || '',
        position_pt: exp.position_pt || '',
        position_en: exp.position_en || '',
        start_date: exp.start_date || '',
        end_date: exp.end_date || '',
        current: !!exp.current,
        description_pt: exp.description_pt || '',
        description_en: exp.description_en || '',
        description_bullets_pt: Array.isArray(exp.description_bullets_pt) ? exp.description_bullets_pt : [],
        description_bullets_en: Array.isArray(exp.description_bullets_en) ? exp.description_bullets_en : [],
        technologies: techs,
        technologies_text: techText,
        competencies: [],
        projects,
      };
    };

    const matchLanguage = (l) => {
      const name = (l.name_pt || l.name_en || '').toLowerCase();
      const match = AVAILABLE_LANGUAGES.find(al => 
        al.name_pt.toLowerCase() === name || al.name_en.toLowerCase() === name
      );
      return {
        id: match ? match.id : `lang-${Date.now()}-${Math.random().toString(36).substr(2,5)}`,
        name_pt: match ? match.name_pt : (l.name_pt || ''),
        name_en: match ? match.name_en : (l.name_en || ''),
        level: l.level || 'B1'
      };
    };

    return {
      name: data.name || '',
      function_title: data.function_title || '',
      career_start_year: parseInt(data.career_start_year) || null,
      summary_pt: data.summary_pt || '',
      summary_en: data.summary_en || '',
      availability_pt: data.availability_pt || '',
      availability_en: data.availability_en || '',
      experiences: Array.isArray(data.experiences) ? data.experiences.map(normalizeExp) : [],
      education: Array.isArray(data.education) ? data.education : [],
      certifications: Array.isArray(data.certifications) ? data.certifications : [],
      languages: Array.isArray(data.languages_detected)
        ? data.languages_detected.map(matchLanguage).filter((l, i, arr) => arr.findIndex(x => x.id === l.id) === i)
        : [],
      detected_skills: Array.isArray(data.detected_skills) ? data.detected_skills : []
    };
  };

  const applyImportedData = (data) => {
    const normalized = normalizeImportedData(data);
    setCV(prev => ({
      ...prev,
      name: normalized.name || prev.name,
      function_title: normalized.function_title || prev.function_title,
      career_start_year: normalized.career_start_year || prev.career_start_year,
      summary_pt: normalized.summary_pt || prev.summary_pt,
      summary_en: normalized.summary_en || prev.summary_en,
      availability_pt: normalized.availability_pt || prev.availability_pt,
      availability_en: normalized.availability_en || prev.availability_en,
      experiences: normalized.experiences.length > 0 ? normalized.experiences : prev.experiences,
      education: normalized.education.length > 0 ? normalized.education : prev.education,
      certifications: normalized.certifications.length > 0 ? normalized.certifications : prev.certifications,
      languages: normalized.languages.length > 0 ? normalized.languages : prev.languages
    }));

    const expCount = normalized.experiences.length;
    if (expCount > 0) {
      toast.success(
        language === 'pt' 
          ? `CV importado com sucesso! ${expCount} experiência(s) profissional(ais) encontrada(s).` 
          : `CV imported successfully! ${expCount} professional experience(s) found.`
      );
    } else {
      toast.success(language === 'pt' ? 'CV importado com sucesso!' : 'CV imported successfully!');
    }

    if (normalized.detected_skills.length > 0) {
      toast.info(`${language === 'pt' ? 'Competências detectadas' : 'Detected skills'}: ${normalized.detected_skills.slice(0, 5).join(', ')}...`);
    }
  };

  const handleImportCV = async () => {
    if (!importFile) {
      toast.error(language === 'pt' ? 'Selecione um ficheiro' : 'Select a file');
      return;
    }
    setImporting(true);
    toast.info(language === 'pt' ? 'A processar CV com IA...' : 'Processing CV with AI...');
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('lang', language);

      // Kick off the async job. Backend returns 202 with { job_id, status } immediately.
      const startResp = await axios.post(`${API}/ai/import-cv-file`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // just for the initial file upload + text extraction
      });
      const jobId = startResp.data?.job_id;
      if (!jobId) {
        toast.error(language === 'pt' ? 'Erro ao iniciar importação' : 'Failed to start import');
        return;
      }

      // Poll the job every 2s for up to 3 minutes. This bypasses Cloudflare's
      // 100s edge timeout: each poll is a fresh short request.
      const maxPolls = 90; // 90 × 2s = 180s
      let finalData = null;
      let finalError = null;
      for (let i = 0; i < maxPolls; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const s = await axios.get(`${API}/ai/import-jobs/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 20000,
          });
          const status = s.data?.status;
          if (status === 'done') {
            finalData = s.data.result;
            break;
          }
          if (status === 'failed') {
            finalError = s.data.error || (language === 'pt' ? 'Falha na importação' : 'Import failed');
            break;
          }
        } catch (pollErr) {
          // Transient poll error — keep trying.
          console.warn('Poll error (retrying):', pollErr?.message || pollErr);
        }
      }

      if (finalData) {
        try {
          applyImportedData(finalData);
          setShowImportDialog(false);
          setImportFile(null);
          toast.success(language === 'pt' ? 'CV importado com sucesso' : 'CV imported successfully');
        } catch (applyErr) {
          console.error('applyImportedData failed:', applyErr, finalData);
          toast.error(
            language === 'pt'
              ? `Erro ao aplicar dados importados: ${String(applyErr.message || applyErr).slice(0, 140)}`
              : `Failed to apply imported data: ${String(applyErr.message || applyErr).slice(0, 140)}`,
            { duration: 15000 }
          );
        }
      } else if (finalError) {
        toast.error(finalError, { duration: 15000 });
      } else {
        toast.error(language === 'pt' ? 'Tempo excedido — tente novamente' : 'Timed out — please try again', { duration: 15000 });
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      const msg = (typeof detail === 'string' && detail)
        ? detail
        : (language === 'pt' ? 'Erro ao importar CV. Tente novamente.' : 'Error importing CV. Try again.');
      toast.error(status ? `${msg}  (HTTP ${status})` : msg, { duration: 15000 });
      console.error('CV import failed:', { status, detail, error });
    } finally {
      setImporting(false);
    }
  };

  const handleManualImportCV = async () => {
    // Strip HTML to get plain text for AI processing — use DOMParser (safer than
    // assigning to innerHTML, which would execute inline scripts on some browsers).
    const doc = new DOMParser().parseFromString(manualImportText || '', 'text/html');
    const plainText = doc.body?.textContent || '';
    
    if (!plainText.trim()) {
      toast.error(language === 'pt' ? 'Introduza o conteúdo do CV' : 'Enter CV content');
      return;
    }
    
    setManualImporting(true);
    toast.info(language === 'pt' ? 'A processar CV com IA... pode demorar até 2 minutos' : 'Processing CV with AI... may take up to 2 minutes');
    
    try {
      const response = await axios.post(`${API}/ai/import-cv`, {
        text: plainText.trim(),
        lang: language
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 150000
      });
      
      if (response.data.success && response.data.data) {
        applyImportedData(response.data.data);
        setShowManualImportDialog(false);
        setManualImportText('');
      } else {
        toast.error(response.data.error || (language === 'pt' ? 'Erro ao processar CV' : 'Error processing CV'));
      }
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.error(language === 'pt' ? 'Timeout - a IA demorou demasiado. Tente novamente.' : 'Timeout - AI took too long. Try again.');
      } else {
        const msg = error.response?.data?.detail || (language === 'pt' ? 'Erro ao importar CV. Tente novamente.' : 'Error importing CV. Try again.');
        toast.error(msg);
      }
    } finally {
      setManualImporting(false);
    }
  };

  const handlePreview = async () => {
    if (!cvId) {
      toast.error(language === 'pt' ? 'Guarde o CV primeiro' : 'Save CV first');
      return;
    }
    setPreviewing(true);
    try {
      const exportLang = cv.cv_language || language || 'pt';
      const response = await axios.get(`${API}/cvs/${cvId}/preview/${exportLang}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      // Revoke previous URL if exists
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(url);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleExport = async (langOverride) => {
    if (!cvId) {
      toast.error(language === 'pt' ? 'Guarde o CV primeiro' : 'Save CV first');
      return;
    }
    const exportLang = langOverride || cv.cv_language || language || 'pt';
    try {
      setExporting(true);
      // Direct browser download via Content-Disposition: attachment.
      // Works reliably in Safari/iOS where blob-based JS downloads fail.
      const url = `${API}/cvs/${cvId}/download/${exportLang}?token=${encodeURIComponent(token)}`;
      const ifr = document.createElement('iframe');
      ifr.style.display = 'none';
      ifr.src = url;
      document.body.appendChild(ifr);
      setTimeout(() => ifr.remove(), 60000);
      setShowExportLangDialog(false);
      toast.success(language === 'pt' ? 'CV exportado com sucesso!' : 'CV exported successfully!');
    } catch (error) {
      const detail = error?.message || 'unknown';
      const msg = language === 'pt' ? `Erro ao exportar: ${detail}` : `Export failed: ${detail}`;
      console.error('CV export failed:', { cvId, exportLang, error });
      toast.error(msg, { duration: 12000 });
    } finally {
      setExporting(false);
    }
  };

  const handleExportWordClick = () => {
    if (!cvId) {
      toast.error(language === 'pt' ? 'Guarde o CV primeiro' : 'Save CV first');
      return;
    }
    setShowExportLangDialog(true);
  };

  const updateHeaderConfig = (field, value) => {
    setCV({ ...cv, header_config: { ...cv.header_config, [field]: value } });
  };

  // Bulk add competencies
  const toggleBulkCompetency = (compId) => {
    setSelectedBulkComps(prev => 
      prev.includes(compId) ? prev.filter(id => id !== compId) : [...prev, compId]
    );
  };

  const handleRequestNewCompetency = async (name, index) => {
    try {
      const response = await axios.post(`${API}/competency-requests`, {
        name_pt: name,
        name_en: name,
        category: 'technical'
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      const data = response.data;
      if (data.already_exists) {
        // Competency exists - use it directly
        const updated = [...cv.main_competencies];
        updated[index] = { ...updated[index], competency_id: data.competency_id, name_pt: name, name_en: name };
        setCV({ ...cv, main_competencies: updated });
        toast.success(language === 'pt' ? 'Competência encontrada e adicionada!' : 'Competency found and added!');
      } else if (data.already_pending) {
        toast.info(language === 'pt' ? 'Pedido já submetido e aguarda aprovação do administrador' : 'Request already submitted, awaiting admin approval');
        const updated = [...cv.main_competencies];
        updated[index] = { ...updated[index], competency_id: '', name_pt: name, name_en: name };
        setCV({ ...cv, main_competencies: updated });
      } else {
        toast.success(language === 'pt' ? 'Pedido de nova competência submetido! Aguarda aprovação do administrador.' : 'New competency request submitted! Awaiting admin approval.');
        const updated = [...cv.main_competencies];
        updated[index] = { ...updated[index], competency_id: '', name_pt: name, name_en: name };
        setCV({ ...cv, main_competencies: updated });
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao submeter pedido' : 'Erro ao submeter pedido');
    }
  };

  // Submit a competency-request for a TECHNOLOGY used inside an experience.
  // If the name already exists as a competency we attach it directly; otherwise
  // we keep the typed name and notify the admin queue.
  const handleRequestNewExperienceTech = async (name, expIndex, techIndex) => {
    try {
      const response = await axios.post(`${API}/competency-requests`, {
        name_pt: name, name_en: name, category: 'technical',
      }, { headers: { Authorization: `Bearer ${token}` } });
      const data = response.data;
      const updated = [...cv.experiences];
      const techs = [...(updated[expIndex].technologies || [])];
      const current = techs[techIndex] || { name: '', level: 3 };
      if (data.already_exists) {
        techs[techIndex] = { ...current, name, competency_id: data.competency_id };
        toast.success(language === 'pt' ? 'Competência encontrada e adicionada!' : 'Competency found and added!');
      } else if (data.already_pending) {
        techs[techIndex] = { ...current, name, competency_id: '' };
        toast.info(language === 'pt' ? 'Pedido já submetido e aguarda aprovação do administrador' : 'Request already submitted, awaiting admin approval');
      } else {
        techs[techIndex] = { ...current, name, competency_id: '' };
        toast.success(language === 'pt' ? 'Pedido de nova competência submetido! Aguarda aprovação do administrador.' : 'New competency request submitted! Awaiting admin approval.');
      }
      updated[expIndex].technologies = techs;
      setCV({ ...cv, experiences: updated });
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao submeter pedido' : 'Erro ao submeter pedido');
    }
  };

  const handleAddBulkCompetencies = () => {
    if (selectedBulkComps.length === 0) return;
    
    const newComps = selectedBulkComps
      .filter(id => !cv.main_competencies.some(c => c.competency_id === id))
      .map(id => {
        const comp = competencies.find(c => c.id === id);
        return comp ? { competency_id: id, name_pt: comp.name_pt, name_en: comp.name_en, level: 3 } : null;
      })
      .filter(Boolean);
    
    setCV({ ...cv, main_competencies: [...cv.main_competencies, ...newComps] });
    setSelectedBulkComps([]);
    setShowBulkCompDialog(false);
    toast.success(language === 'pt' ? `${newComps.length} competências adicionadas` : `${newComps.length} competencies added`);
  };

  const addMainCompetency = () => {
    setCV({ ...cv, main_competencies: [...cv.main_competencies, { competency_id: '', name_pt: '', name_en: '', level: 3 }] });
  };

  const updateMainCompetency = (index, field, value) => {
    const updated = [...cv.main_competencies];
    if (field === 'competency_id') {
      const comp = competencies.find(c => c.id === value);
      if (comp) updated[index] = { ...updated[index], competency_id: value, name_pt: comp.name_pt, name_en: comp.name_en };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setCV({ ...cv, main_competencies: updated });
  };

  const removeMainCompetency = (index) => {
    setCV({ ...cv, main_competencies: cv.main_competencies.filter((_, i) => i !== index) });
  };

  const moveMainCompetency = (index, direction) => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= cv.main_competencies.length) return;
    const updated = [...cv.main_competencies];
    [updated[index], updated[target]] = [updated[target], updated[index]];
    setCV({ ...cv, main_competencies: updated });
  };

  const _newEmptyExperience = () => ({
    company: '', position_pt: '', position_en: '', start_date: '', end_date: '',
    current: false, description_pt: '', description_en: '',
    description_bullets_pt: [], description_bullets_en: [],
    technologies: [],
    technologies_text: '',
    competencies: [],
    competencies_text: ''
  });

  const addExperience = () => {
    setCV({ ...cv, experiences: [...cv.experiences, _newEmptyExperience()] });
  };

  const addExperienceAt = (index) => {
    // Insert a fresh empty experience BEFORE the given index (i.e. above it).
    const updated = [...cv.experiences];
    updated.splice(index, 0, _newEmptyExperience());
    setCV({ ...cv, experiences: updated });
  };

  const moveExperience = (index, direction) => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= cv.experiences.length) return;
    const updated = [...cv.experiences];
    [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
    setCV({ ...cv, experiences: updated });
  };

  const updateExperience = (index, field, value) => {
    const updated = [...cv.experiences];
    updated[index] = { ...updated[index], [field]: value };
    setCV({ ...cv, experiences: updated });
  };

  const removeExperience = (index) => {
    setCV({ ...cv, experiences: cv.experiences.filter((_, i) => i !== index) });
  };

  const addExperienceCompetency = (expIndex) => {
    const updated = [...cv.experiences];
    updated[expIndex].competencies = [...updated[expIndex].competencies, { competency_id: '', name_pt: '', name_en: '', level: 3 }];
    setCV({ ...cv, experiences: updated });
  };

  const updateExperienceCompetency = (expIndex, compIndex, field, value) => {
    const updated = [...cv.experiences];
    if (field === 'competency_id') {
      const comp = competencies.find(c => c.id === value);
      if (comp) {
        updated[expIndex].competencies[compIndex] = { ...updated[expIndex].competencies[compIndex], competency_id: value, name_pt: comp.name_pt, name_en: comp.name_en };
      }
    } else {
      updated[expIndex].competencies[compIndex] = { ...updated[expIndex].competencies[compIndex], [field]: value };
    }
    setCV({ ...cv, experiences: updated });
  };

  const removeExperienceCompetency = (expIndex, compIndex) => {
    const updated = [...cv.experiences];
    updated[expIndex].competencies = updated[expIndex].competencies.filter((_, i) => i !== compIndex);
    setCV({ ...cv, experiences: updated });
  };

  // Bullet points management for experience descriptions
  const addDescriptionBullet = (expIndex) => {
    const updated = [...cv.experiences];
    const bulletField = language === 'pt' ? 'description_bullets_pt' : 'description_bullets_en';
    if (!updated[expIndex][bulletField]) {
      updated[expIndex][bulletField] = [];
    }
    updated[expIndex][bulletField] = [...updated[expIndex][bulletField], ''];
    setCV({ ...cv, experiences: updated });
  };

  const updateDescriptionBullet = (expIndex, bulletIndex, value) => {
    const updated = [...cv.experiences];
    const bulletField = language === 'pt' ? 'description_bullets_pt' : 'description_bullets_en';
    if (!updated[expIndex][bulletField]) {
      updated[expIndex][bulletField] = [];
    }
    updated[expIndex][bulletField][bulletIndex] = value;
    setCV({ ...cv, experiences: updated });
  };

  const removeDescriptionBullet = (expIndex, bulletIndex) => {
    const updated = [...cv.experiences];
    const bulletField = language === 'pt' ? 'description_bullets_pt' : 'description_bullets_en';
    updated[expIndex][bulletField] = updated[expIndex][bulletField].filter((_, i) => i !== bulletIndex);
    setCV({ ...cv, experiences: updated });
  };

  // Technologies management for each experience
  const addExperienceTechnology = (expIndex) => {
    const updated = [...cv.experiences];
    if (!updated[expIndex].technologies) {
      updated[expIndex].technologies = [];
    }
    updated[expIndex].technologies = [...updated[expIndex].technologies, { name: '', level: 3 }];
    setCV({ ...cv, experiences: updated });
  };

  const updateExperienceTechnology = (expIndex, techIndex, field, value) => {
    const updated = [...cv.experiences];
    updated[expIndex].technologies[techIndex] = { 
      ...updated[expIndex].technologies[techIndex], 
      [field]: value 
    };
    setCV({ ...cv, experiences: updated });
  };

  const removeExperienceTechnology = (expIndex, techIndex) => {
    const updated = [...cv.experiences];
    updated[expIndex].technologies = updated[expIndex].technologies.filter((_, i) => i !== techIndex);
    setCV({ ...cv, experiences: updated });
  };

  const addEducation = () => {
    setCV({ ...cv, education: [...cv.education, { institution: '', degree_pt: '', degree_en: '', field_pt: '', field_en: '', start_year: '', end_year: '' }] });
  };

  const updateEducation = (index, field, value) => {
    const updated = [...cv.education];
    updated[index] = { ...updated[index], [field]: value };
    setCV({ ...cv, education: updated });
  };

  const removeEducation = (index) => {
    setCV({ ...cv, education: cv.education.filter((_, i) => i !== index) });
  };

  const addCertification = () => {
    setCV({ ...cv, certifications: [...cv.certifications, { name: '', issuer: '', year: '' }] });
  };

  const updateCertification = (index, field, value) => {
    const updated = [...cv.certifications];
    updated[index] = { ...updated[index], [field]: value };
    setCV({ ...cv, certifications: updated });
  };

  const removeCertification = (index) => {
    setCV({ ...cv, certifications: cv.certifications.filter((_, i) => i !== index) });
  };

  const toggleLanguage = (langId) => {
    const existing = cv.languages.find(l => l.id === langId);
    if (existing) {
      setCV({ ...cv, languages: cv.languages.filter(l => l.id !== langId) });
    } else {
      const langData = AVAILABLE_LANGUAGES.find(l => l.id === langId);
      setCV({ ...cv, languages: [...cv.languages, { ...langData, level: 'B1' }] });
    }
  };

  const updateLanguageLevel = (langId, level) => {
    setCV({ ...cv, languages: cv.languages.map(l => l.id === langId ? { ...l, level } : l) });
  };

  const filteredCompetencies = useMemo(() => 
    selectedCategory === 'all' ? competencies : competencies.filter(c => c.category === selectedCategory),
    [competencies, selectedCategory]
  );

  if (!language) return <LanguageSelector onSelect={chooseLanguage} />;
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="loading-spinner"></div></div>;

  return (
    <div className="min-h-screen bg-[#f8fafc] py-8 px-4" data-testid="cv-builder-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {cvId ? (language === 'pt' ? 'Editar CV' : 'Edit CV') : t('createCV')}
            </h1>
            <p className="text-[#64748b]">
              {language === 'pt' ? 'Formato' : 'Format'}: <span className="font-medium text-[#1E3C3C]">{language === 'pt' ? 'Português' : 'English'}</span>
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowImportDialog(true)} className="border-[#1E3C3C] text-[#1E3C3C]" data-testid="import-cv-btn">
              <Import className="w-4 h-4 mr-2" />
              {language === 'pt' ? 'Importar CV (Ficheiro)' : 'Import CV (File)'}
            </Button>
            <Button variant="outline" onClick={() => setShowManualImportDialog(true)} className="border-[#1E3C3C] text-[#1E3C3C]" data-testid="manual-import-cv-btn">
              <PenLine className="w-4 h-4 mr-2" />
              {language === 'pt' ? 'Importar CV (Manual)' : 'Import CV (Manual)'}
            </Button>
            <Button
              variant="outline"
              onClick={handleFixLanguage}
              disabled={fixingLanguage}
              className="border-[#1E3C3C] text-[#1E3C3C] hover:bg-[#1E3C3C]/10"
              data-testid="fix-language-btn"
              title={language === 'pt' ? 'Traduz para PT-PT qualquer conteúdo em inglês ou português do Brasil' : 'Translates any English or Brazilian PT content to European PT'}
            >
              {fixingLanguage ? <div className="loading-spinner w-4 h-4 mr-2"></div> : <Sparkles className="w-4 h-4 mr-2" />}
              {fixingLanguage
                ? (language === 'pt' ? 'A corrigir...' : 'Fixing...')
                : (language === 'pt' ? 'IA: Corrigir para PT-PT' : 'AI: Fix to PT-PT')}
            </Button>
            <Button
              variant="outline"
              onClick={handleClearCV}
              className="border-[#dc2626] text-[#dc2626] hover:bg-[#dc2626]/10 hover:text-[#dc2626]"
              data-testid="clear-cv-btn"
              title={language === 'pt' ? 'Limpa todos os campos deste CV' : 'Clears all fields in this CV'}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {language === 'pt' ? 'Limpar CV' : 'Clear CV'}
            </Button>
          </div>
        </div>

        <div className="cv-builder-container">
          {/* Form Section */}
          <div className="cv-form-section" data-testid="cv-form">
            <Accordion type="multiple" defaultValue={['personal', 'languages', 'competencies', 'summary']} className="space-y-4">
              
              {/* Header Config section removed (Feb 2026 user spec): the
                  per-CV header (logo, client, font_family) is now centrally
                  managed via the global template / Backoffice. Keeping the
                  fields on the data model with safe defaults so the export
                  service still finds them. */}

              {/* Personal Info */}
              <AccordionItem value="personal" className="form-section">
                <AccordionTrigger className="form-section-title hover:no-underline">
                  <User className="w-5 h-5" />
                  {t('personalInfo')}
                </AccordionTrigger>
                <AccordionContent className="pt-4 space-y-4">
                  <div className="p-3 bg-[#FFFBEB] border border-[#FFD700]/40 rounded-lg">
                    <Label className="text-xs font-medium text-[#B8860B]">
                      {language === 'pt' ? 'Tipo de CV' : 'CV Type'}
                    </Label>
                    <div className="flex gap-2 mt-2" data-testid="cv-type-toggle">
                      <button
                        type="button"
                        onClick={() => setCV({ ...cv, cv_type: 'tech' })}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${cv.cv_type !== 'rl' ? 'bg-[#1E3C3C] text-white' : 'bg-white text-[#1E3C3C] border border-[#1E3C3C]/30'}`}
                        data-testid="cv-type-tech"
                      >
                        {language === 'pt' ? 'Técnico (com Experiência)' : 'Technical (with Experience)'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setCV({ ...cv, cv_type: 'rl' })}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${cv.cv_type === 'rl' ? 'bg-[#1E3C3C] text-white' : 'bg-white text-[#1E3C3C] border border-[#1E3C3C]/30'}`}
                        data-testid="cv-type-rl"
                      >
                        RL ({language === 'pt' ? 'Recém-Licenciado' : 'Recent Graduate'})
                      </button>
                    </div>
                    <p className="text-xs text-[#64748b] mt-2">
                      {language === 'pt'
                        ? cv.cv_type === 'rl'
                          ? 'CV de Recém-Licenciado: a secção de Experiência Profissional fica oculta na edição e na exportação.'
                          : 'CV técnico padrão com Experiência Profissional.'
                        : cv.cv_type === 'rl'
                          ? 'Recent Graduate CV: Professional Experience section is hidden in editing and export.'
                          : 'Standard technical CV with Professional Experience.'}
                    </p>
                  </div>
                  <div>
                    <Label>{t('nameId')}</Label>
                    <Input value={cv.name} onChange={(e) => setCV({ ...cv, name: e.target.value })} placeholder={language === 'pt' ? 'Nome ou ID do candidato' : 'Candidate name or ID'} className="mt-1" data-testid="name-input" />
                  </div>
                  <div>
                    <Label>{language === 'pt' ? 'Função' : 'Job Function'}</Label>
                    <div className="relative mt-1">
                      <Input
                        value={cv.function_title || ''}
                        onChange={(e) => setCV({ ...cv, function_title: e.target.value })}
                        placeholder={language === 'pt' ? 'Pesquisar ou escrever função...' : 'Search or type function...'}
                        className="input-focus"
                        data-testid="function-input"
                        list="function-options"
                      />
                      <datalist id="function-options">
                        {itFunctions.map(f => (
                          <option key={f.id} value={language === 'pt' ? f.name_pt : f.name_en} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('startYear')}</Label>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <Select
                          value={String(cv.career_start_month || 1)}
                          onValueChange={(value) => setCV({ ...cv, career_start_month: parseInt(value) })}
                        >
                          <SelectTrigger data-testid="start-month-select">
                            <SelectValue placeholder={language === 'pt' ? 'Mês' : 'Month'} />
                          </SelectTrigger>
                          <SelectContent>
                            {[
                              { value: '1', label_pt: 'Janeiro', label_en: 'January' },
                              { value: '2', label_pt: 'Fevereiro', label_en: 'February' },
                              { value: '3', label_pt: 'Março', label_en: 'March' },
                              { value: '4', label_pt: 'Abril', label_en: 'April' },
                              { value: '5', label_pt: 'Maio', label_en: 'May' },
                              { value: '6', label_pt: 'Junho', label_en: 'June' },
                              { value: '7', label_pt: 'Julho', label_en: 'July' },
                              { value: '8', label_pt: 'Agosto', label_en: 'August' },
                              { value: '9', label_pt: 'Setembro', label_en: 'September' },
                              { value: '10', label_pt: 'Outubro', label_en: 'October' },
                              { value: '11', label_pt: 'Novembro', label_en: 'November' },
                              { value: '12', label_pt: 'Dezembro', label_en: 'December' },
                            ].map(month => (
                              <SelectItem key={month.value} value={month.value}>
                                {language === 'pt' ? month.label_pt : month.label_en}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input 
                          type="number" 
                          min="1950" 
                          max={new Date().getFullYear()} 
                          value={cv.career_start_year} 
                          onChange={(e) => setCV({ ...cv, career_start_year: parseInt(e.target.value) || new Date().getFullYear() })} 
                          placeholder={language === 'pt' ? 'Ano' : 'Year'}
                          data-testid="start-year-input" 
                        />
                      </div>
                    </div>
                    <div>
                      <Label>{t('calculatedExperience')}</Label>
                      <div className="mt-1 space-y-2">
                        <div className="h-10 px-3 py-2 bg-[#f0fdfa] border border-[#1E3C3C] rounded-md flex items-center justify-between">
                          <span className="text-[#1E3C3C] font-semibold">
                            {cv.years_experience} {t('years')} {cv.months_experience > 0 && (() => {
                              // Extracted to avoid an unreadable nested ternary.
                              const isPT = language === 'pt';
                              const conjunction = isPT ? 'e' : 'and';
                              let unit;
                              if (isPT) {
                                unit = cv.months_experience === 1 ? 'mês' : 'meses';
                              } else {
                                unit = cv.months_experience === 1 ? 'month' : 'months';
                              }
                              return <>{conjunction} {cv.months_experience} {unit}</>;
                            })()}
                          </span>
                        </div>
                        <div>
                          <Label className="text-xs text-[#64748b]">{language === 'pt' ? 'Editar Manualmente' : 'Edit Manually'}</Label>
                          <Input 
                            value={cv.experience_override || ''} 
                            onChange={(e) => setCV({ ...cv, experience_override: e.target.value })}
                            placeholder={language === 'pt' ? 'Ex: 5 anos e 3 meses' : 'Ex: 5 years and 3 months'}
                            className="mt-1 text-sm"
                            data-testid="experience-override-input"
                          />
                          <p className="text-xs text-[#64748b] mt-1">
                            {language === 'pt' 
                              ? 'Deixe em branco para usar o valor calculado automaticamente' 
                              : 'Leave blank to use the automatically calculated value'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>{t('availability')}</Label>
                    <Select 
                      value={cv.availability_pt || ''} 
                      onValueChange={(value) => {
                        const option = availabilityOptions.find(o => o.name_pt === value);
                        setCV({ 
                          ...cv, 
                          availability_pt: option?.name_pt || value, 
                          availability_en: option?.name_en || value 
                        });
                      }}
                    >
                      <SelectTrigger className="mt-1" data-testid="availability-select">
                        <SelectValue placeholder={language === 'pt' ? 'Selecione a disponibilidade' : 'Select availability'}>
                          {cv.availability_pt ? (language === 'pt' ? cv.availability_pt : cv.availability_en) : ''}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {availabilityOptions.map(option => (
                          <SelectItem key={option.id} value={option.name_pt}>
                            {language === 'pt' ? option.name_pt : option.name_en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{language === 'pt' ? 'Score Evolutt' : 'Score Evolutt'}</Label>
                    <div className="mt-1 flex items-center gap-3">
                      <Input 
                        type="number" 
                        min="0" 
                        max="100" 
                        value={cv.score_evolutt ?? ''} 
                        onChange={(e) => setCV({ ...cv, score_evolutt: e.target.value ? parseInt(e.target.value) : null })} 
                        placeholder="0-100"
                        className="w-24"
                        data-testid="score-evolutt-input"
                      />
                      <span className="text-[#64748b]">%</span>
                      {cv.score_evolutt !== null && cv.score_evolutt !== undefined && (
                        <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                          <div 
                            className="h-full rounded-full transition-all" 
                            style={{ 
                              width: `${cv.score_evolutt}%`,
                              backgroundColor: '#1E3C3C'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Languages */}
              <AccordionItem value="languages" className="form-section">
                <AccordionTrigger className="form-section-title hover:no-underline">
                  <Languages className="w-5 h-5" />
                  {t('languages')}
                </AccordionTrigger>
                <AccordionContent className="pt-4 space-y-4">
                  {AVAILABLE_LANGUAGES.map((lang) => {
                    const selected = cv.languages.find(l => l.id === lang.id);
                    return (
                      <div key={lang.id} className="flex items-center gap-4 p-3 bg-[#f8fafc] rounded-lg">
                        <div className="flex items-center gap-3 flex-1">
                          <Checkbox id={`lang-${lang.id}`} checked={!!selected} onCheckedChange={() => toggleLanguage(lang.id)} />
                          <Label htmlFor={`lang-${lang.id}`} className="cursor-pointer font-medium">{language === 'pt' ? lang.name_pt : lang.name_en}</Label>
                        </div>
                        {selected && (
                          <Select value={selected.level} onValueChange={(value) => updateLanguageLevel(lang.id, value)}>
                            <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                            <SelectContent>{LANGUAGE_LEVELS.map(level => <SelectItem key={level} value={level}>{level}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })}
                </AccordionContent>
              </AccordionItem>

              {/* Main Competencies */}
              <AccordionItem value="competencies" className="form-section">
                <AccordionTrigger className="form-section-title hover:no-underline">
                  <Award className="w-5 h-5" />
                  {t('mainCompetencies')}
                </AccordionTrigger>
                <AccordionContent className="pt-4 space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Label className="text-sm">{language === 'pt' ? 'Filtrar:' : 'Filter:'}</Label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{language === 'pt' ? 'Todas' : 'All'}</SelectItem>
                        {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={() => setShowBulkCompDialog(true)} className="ml-auto text-[#1E3C3C] border-[#1E3C3C]" data-testid="bulk-add-btn">
                      <ListPlus className="w-4 h-4 mr-1" />
                      {language === 'pt' ? 'Adicionar Várias' : 'Add Multiple'}
                    </Button>
                  </div>

                  {cv.main_competencies.map((comp, index) => (
                    <div key={`comp-${index}`} className="flex items-center gap-4 p-3 bg-[#f8fafc] rounded-lg">
                      <div className="flex-1">
                        <CompetencySearchInput 
                          value={comp.competency_id ? (language === 'pt' ? comp.name_pt : comp.name_en) : (comp.name_pt || comp.name_en || '')}
                          competencies={filteredCompetencies}
                          language={language}
                          onSelect={(compData) => {
                            const updated = [...cv.main_competencies];
                            updated[index] = { ...updated[index], competency_id: compData.id, name_pt: compData.name_pt, name_en: compData.name_en };
                            setCV({ ...cv, main_competencies: updated });
                          }}
                          onRequestNew={(name) => handleRequestNewCompetency(name, index)}
                          placeholder={t('selectCompetency')}
                        />
                      </div>
                      <div className="flex-shrink-0">
                        <CompetencyLevel
                          level={comp.level ?? 3}
                          onChange={(level) => {
                            const updated = [...cv.main_competencies];
                            updated[index] = { ...updated[index], level };
                            setCV({ ...cv, main_competencies: updated });
                          }}
                        />
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => moveMainCompetency(index, 'up')} disabled={index === 0} title={language === 'pt' ? 'Mover para cima' : 'Move up'} data-testid={`comp-move-up-${index}`}>
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => moveMainCompetency(index, 'down')} disabled={index === cv.main_competencies.length - 1} title={language === 'pt' ? 'Mover para baixo' : 'Move down'} data-testid={`comp-move-down-${index}`}>
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => removeMainCompetency(index)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" onClick={addMainCompetency} className="w-full border-dashed">
                    <Plus className="w-4 h-4 mr-2" />{t('addCompetency')}
                  </Button>
                </AccordionContent>
              </AccordionItem>

              {/* Professional Summary with AI */}
              <AccordionItem value="summary" className="form-section">
                <AccordionTrigger className="form-section-title hover:no-underline">
                  <FileText className="w-5 h-5" />
                  {t('professionalSummary')}
                </AccordionTrigger>
                <AccordionContent className="pt-4 space-y-4">
                  <div className="flex flex-col sm:flex-row justify-end gap-2 items-stretch sm:items-center">
                    <Select value={summaryStyle} onValueChange={setSummaryStyle} disabled={generatingSummary}>
                      <SelectTrigger className="w-full sm:w-[180px]" data-testid="summary-style-select">
                        <SelectValue placeholder={language === 'pt' ? 'Estilo' : 'Style'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="direto" data-testid="style-direto">
                          {language === 'pt' ? 'Direto (análise)' : 'Direct (analysis)'}
                        </SelectItem>
                        <SelectItem value="impacto" data-testid="style-impacto">
                          {language === 'pt' ? 'Impacto (verbos de ação)' : 'Impact (action verbs)'}
                        </SelectItem>
                        <SelectItem value="conciso" data-testid="style-conciso">
                          {language === 'pt' ? 'Conciso (2 frases)' : 'Concise (2 sentences)'}
                        </SelectItem>
                        <SelectItem value="narrativo" data-testid="style-narrativo">
                          {language === 'pt' ? 'Narrativo (trajectória)' : 'Narrative (trajectory)'}
                        </SelectItem>
                        <SelectItem value="senior" data-testid="style-senior">
                          {language === 'pt' ? 'Sénior (liderança)' : 'Senior (leadership)'}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={handleGenerateSummary} disabled={generatingSummary} className="text-[#1E3C3C] border-[#1E3C3C]" data-testid="generate-summary-btn">
                      {generatingSummary ? <div className="loading-spinner w-4 h-4 mr-2"></div> : <Sparkles className="w-4 h-4 mr-2" />}
                      {generatingSummary
                        ? (language === 'pt' ? 'A gerar 3 opções...' : 'Generating 3 options...')
                        : (language === 'pt' ? 'Gerar 3 opções com IA' : 'Generate 3 options with AI')}
                    </Button>
                  </div>
                  <AutoGrowTextarea
                    value={language === 'pt' ? cv.summary_pt : cv.summary_en}
                    onChange={(e) => setCV({ ...cv, summary_pt: language === 'pt' ? e.target.value : cv.summary_pt, summary_en: language === 'pt' ? '' : e.target.value })}
                    placeholder={language === 'pt' ? 'O resumo será gerado automaticamente com base na experiência...' : 'Summary will be auto-generated based on experience...'}
                    data-testid="summary-input"
                  />
                </AccordionContent>
              </AccordionItem>

              {/* Experience — hidden for 'rl' (Recém-Licenciado) CV type. */}
              {cv.cv_type !== 'rl' && (
              <AccordionItem value="experience" className="form-section">
                <AccordionTrigger className="form-section-title hover:no-underline">
                  <Briefcase className="w-5 h-5" />
                  {t('professionalExperience')}
                </AccordionTrigger>
                <AccordionContent className="pt-4 space-y-4">
                  {cv.experiences.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => addExperienceAt(0)}
                      className="w-full border-dashed"
                      data-testid="add-experience-top-btn"
                    >
                      <ArrowUpFromLine className="w-4 h-4 mr-2" />
                      {language === 'pt' ? 'Adicionar experiência no topo' : 'Add experience at the top'}
                    </Button>
                  )}
                  {cv.experiences.map((exp, expIndex) => (
                    // NOTE: key uses index — content-derived keys broke input
                    // focus because typing in `company` / `position` / `start_date`
                    // would change the key on every keystroke, causing React to
                    // unmount and remount the entire experience card (and the
                    // input the user was typing into).
                    <div key={`exp-${expIndex}`} className="experience-card">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="font-medium text-[#0f172a]">{exp.company || (language === 'pt' ? 'Nova Experiência' : 'New Experience')}</h4>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => moveExperience(expIndex, 'up')}
                            disabled={expIndex === 0}
                            title={language === 'pt' ? 'Mover para cima' : 'Move up'}
                            data-testid={`exp-move-up-${expIndex}`}
                          >
                            <ArrowUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => moveExperience(expIndex, 'down')}
                            disabled={expIndex === cv.experiences.length - 1}
                            title={language === 'pt' ? 'Mover para baixo' : 'Move down'}
                            data-testid={`exp-move-down-${expIndex}`}
                          >
                            <ArrowDown className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => addExperienceAt(expIndex)}
                            title={language === 'pt' ? 'Inserir experiência acima' : 'Insert experience above'}
                            data-testid={`exp-insert-above-${expIndex}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeExperience(expIndex)}
                            className="text-red-500"
                            title={language === 'pt' ? 'Remover' : 'Remove'}
                            data-testid={`exp-remove-${expIndex}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <Input placeholder={t('company')} value={exp.company} onChange={(e) => updateExperience(expIndex, 'company', e.target.value)} data-testid={`exp-company-${expIndex}`} />
                        <Input placeholder={t('position')} value={language === 'pt' ? exp.position_pt : exp.position_en} onChange={(e) => updateExperience(expIndex, language === 'pt' ? 'position_pt' : 'position_en', e.target.value)} data-testid={`exp-position-${expIndex}`} />
                        <div className="grid grid-cols-2 gap-3">
                          <Input type="month" value={exp.start_date} onChange={(e) => updateExperience(expIndex, 'start_date', e.target.value)} />
                          <Input type="month" value={exp.end_date} onChange={(e) => updateExperience(expIndex, 'end_date', e.target.value)} disabled={exp.current} />
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={exp.current} onChange={(e) => updateExperience(expIndex, 'current', e.target.checked)} className="rounded" />
                          {t('currentPosition')}
                        </label>
                        
                        {/* Description Bullets Section */}
                        <div className="mt-4 pt-4 border-t border-[#e2e8f0]">
                          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                            <Label className="text-sm font-medium text-[#0f172a]">
                              {language === 'pt' ? 'Descrição (Bullet Points)' : 'Description (Bullet Points)'}
                            </Label>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-xs text-[#1E3C3C] hover:bg-[#1E3C3C]/10"
                                data-testid={`suggest-bullets-btn-${expIndex}`}
                                disabled={suggestingBulletsIdx === expIndex}
                                onClick={() => handleSuggestBullets(expIndex)}
                                title={language === 'pt' ? 'IA sugere novas responsabilidades para esta experiência' : 'AI suggests new responsibilities for this experience'}
                              >
                                <Sparkles className="w-3 h-3 mr-1" />
                                {suggestingBulletsIdx === expIndex
                                  ? (language === 'pt' ? 'A gerar…' : 'Generating…')
                                  : (language === 'pt' ? 'IA: mais responsabilidades' : 'AI: more responsibilities')}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-xs text-[#1E3C3C] hover:bg-[#1E3C3C]/10"
                                data-testid={`format-bullets-btn-${expIndex}`}
                                onClick={() => {
                                const field = language === 'pt' ? 'description_bullets_pt' : 'description_bullets_en';
                                const current = (language === 'pt' ? exp.description_bullets_pt : exp.description_bullets_en) || [];
                                const formatted = current.map(line => {
                                  const trimmed = line.trim();
                                  if (!trimmed) return '';
                                  if (trimmed.startsWith('•') || trimmed.startsWith('-')) return trimmed;
                                  return '• ' + trimmed;
                                });
                                updateExperience(expIndex, field, formatted);
                              }}
                            >
                              <List className="w-3 h-3 mr-1" />
                              {language === 'pt' ? 'Formatar Bullets' : 'Format Bullets'}
                            </Button>
                            </div>
                          </div>
                          <p className="text-xs text-[#64748b] mb-3">
                            {language === 'pt' ? 'Escreva a descrição com bullets. Use "•" ou "-" no início de cada linha.' : 'Write description with bullets. Use "•" or "-" at the start of each line.'}
                          </p>
                          <Textarea 
                            value={(language === 'pt' ? exp.description_bullets_pt : exp.description_bullets_en)?.join('\n') || ''}
                            onChange={(e) => {
                              const lines = e.target.value.split('\n');
                              updateExperience(expIndex, language === 'pt' ? 'description_bullets_pt' : 'description_bullets_en', lines);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const target = e.target;
                                const pos = target.selectionStart;
                                const val = target.value;
                                const newVal = val.substring(0, pos) + '\n• ' + val.substring(pos);
                                const field = language === 'pt' ? 'description_bullets_pt' : 'description_bullets_en';
                                updateExperience(expIndex, field, newVal.split('\n'));
                                setTimeout(() => { target.selectionStart = target.selectionEnd = pos + 3; }, 0);
                              }
                            }}
                            placeholder={language === 'pt' 
                              ? '• Primeira responsabilidade ou conquista\n• Segunda responsabilidade\n• Terceira responsabilidade' 
                              : '• First responsibility or achievement\n• Second responsibility\n• Third responsibility'}
                            className="min-h-[150px] font-normal"
                            data-testid={`exp-description-${expIndex}`}
                          />
                        </div>

                        {/* Technologies Section - Interactive list with levels */}
                        <div className="mt-4 pt-4 border-t border-[#e2e8f0]">
                          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                            <Label className="text-sm font-medium text-[#FFD700] flex items-center gap-2">
                              <Award className="w-4 h-4" />
                              {language === 'pt' ? 'Tecnologias Utilizadas' : 'Technologies Used'}
                            </Label>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-xs text-[#1E3C3C] hover:bg-[#1E3C3C]/10 h-7"
                                data-testid={`suggest-tech-btn-${expIndex}`}
                                disabled={suggestingTechIdx === expIndex}
                                onClick={() => handleSuggestTechnologies(expIndex)}
                                title={language === 'pt' ? 'IA sugere tecnologias para esta experiência' : 'AI suggests technologies for this experience'}
                              >
                                <Sparkles className="w-3 h-3 mr-1" />
                                {suggestingTechIdx === expIndex
                                  ? (language === 'pt' ? 'A gerar…' : 'Generating…')
                                  : (language === 'pt' ? 'IA: sugerir' : 'AI: suggest')}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-xs text-[#1E3C3C] hover:bg-[#1E3C3C]/10 h-7"
                                data-testid={`add-tech-btn-${expIndex}`}
                                onClick={() => {
                                  const updated = [...cv.experiences];
                                  updated[expIndex].technologies = [...(updated[expIndex].technologies || []), { name: '', level: 3 }];
                                  setCV({ ...cv, experiences: updated });
                                }}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                {language === 'pt' ? 'Adicionar' : 'Add'}
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {(exp.technologies || []).map((tech, techIndex) => (
                              <div key={`tech-${expIndex}-${techIndex}`} className="flex items-center gap-2" data-testid={`tech-row-${expIndex}-${techIndex}`}>
                                <div className="flex-1">
                                  <CompetencySearchInput
                                    value={tech.name || ''}
                                    competencies={filteredCompetencies}
                                    language={language}
                                    onSelect={(compData) => {
                                      const updated = [...cv.experiences];
                                      updated[expIndex].technologies[techIndex] = {
                                        ...tech,
                                        name: language === 'pt' ? compData.name_pt : compData.name_en,
                                        competency_id: compData.id,
                                      };
                                      setCV({ ...cv, experiences: updated });
                                    }}
                                    onRequestNew={(name) => handleRequestNewExperienceTech(name, expIndex, techIndex)}
                                    placeholder={language === 'pt' ? 'Pesquisar competência…' : 'Search competency…'}
                                  />
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-red-400 hover:text-red-600"
                                  onClick={() => {
                                    const updated = [...cv.experiences];
                                    updated[expIndex].technologies = updated[expIndex].technologies.filter((_, i) => i !== techIndex);
                                    setCV({ ...cv, experiences: updated });
                                  }}
                                  data-testid={`remove-tech-${expIndex}-${techIndex}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                            {(!exp.technologies || exp.technologies.length === 0) && (
                              <p className="text-xs text-[#94a3b8] italic py-2">
                                {language === 'pt' ? 'Sem tecnologias adicionadas. Clique em "Adicionar".' : 'No technologies added. Click "Add".'}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Competencies Section (Optional) */}
                        <div className="mt-4 pt-4 border-t border-[#e2e8f0]">
                          <Label className="text-sm font-medium text-[#1E3C3C]">{t('competencies')} ({language === 'pt' ? 'Opcional' : 'Optional'})</Label>
                          <p className="text-xs text-[#64748b] mb-2">
                            {language === 'pt' ? 'Escreva uma competência por linha (opcional)' : 'Write one competency per line (optional)'}
                          </p>
                          <Textarea 
                            value={(exp.competencies_text || '')}
                            onChange={(e) => updateExperience(expIndex, 'competencies_text', e.target.value)}
                            placeholder={language === 'pt' 
                              ? 'Liderança de equipas\nGestão de projetos\nComunicação' 
                              : 'Team leadership\nProject management\nCommunication'}
                            className="min-h-[80px] font-normal"
                            data-testid={`exp-competencies-${expIndex}`}
                          />
                        </div>

                        {/* Nested Functions & Projects (role progressions, client engagements) */}
                        <div className="mt-4 pt-4 border-t border-[#e2e8f0]">
                          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <Label className="text-sm font-medium text-[#1E3C3C]">
                              {language === 'pt' ? 'Funções & Projetos (opcional)' : 'Functions & Projects (optional)'}
                            </Label>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-xs text-[#1E3C3C] hover:bg-[#1E3C3C]/10 h-7"
                                data-testid={`add-function-btn-${expIndex}`}
                                onClick={() => {
                                  const updated = [...cv.experiences];
                                  updated[expIndex].projects = [
                                    ...(updated[expIndex].projects || []),
                                    {
                                      kind: 'function', client: '', name_pt: '', name_en: '',
                                      start_date: '', end_date: '', current: false,
                                      description_bullets_pt: [], description_bullets_en: [],
                                      technologies: [], technologies_text: '',
                                    },
                                  ];
                                  setCV({ ...cv, experiences: updated });
                                }}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                {language === 'pt' ? 'Adicionar função' : 'Add function'}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-xs text-[#1E3C3C] hover:bg-[#1E3C3C]/10 h-7"
                                data-testid={`add-project-btn-${expIndex}`}
                                onClick={() => {
                                  const updated = [...cv.experiences];
                                  updated[expIndex].projects = [
                                    ...(updated[expIndex].projects || []),
                                    {
                                      kind: 'project', client: '', name_pt: '', name_en: '',
                                      start_date: '', end_date: '', current: false,
                                      description_bullets_pt: [], description_bullets_en: [],
                                      technologies: [], technologies_text: '',
                                    },
                                  ];
                                  setCV({ ...cv, experiences: updated });
                                }}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                {language === 'pt' ? 'Adicionar projeto' : 'Add project'}
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-[#64748b] mb-3">
                            {language === 'pt'
                              ? 'Função = cargo/papel diferente no mesmo empregador. Projeto = engagement/projeto específico (ex: consultoria).'
                              : 'Function = different role at the same employer. Project = specific engagement (e.g., consulting).'}
                          </p>
                          {(exp.projects || []).map((p, projIndex) => {
                            const kind = (p.kind || 'project').toLowerCase();
                            const isFunction = kind === 'function';
                            const kindLabel = isFunction
                              ? (language === 'pt' ? 'Função' : 'Function')
                              : (language === 'pt' ? 'Projeto' : 'Project');
                            return (
                            <div
                              key={`proj-${expIndex}-${projIndex}`}
                              className="mb-3 p-3 bg-[#F8F9FA] border border-[#e2e8f0] rounded-md"
                              data-testid={`project-card-${expIndex}-${projIndex}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-[#B8860B]">
                                  {kindLabel} {projIndex + 1}
                                  {p.name_pt ? <span className="ml-2 text-[#64748b] font-normal">— {p.name_pt}</span> : null}
                                </span>
                                <div className="flex items-center gap-0.5">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-[#64748b] hover:text-[#1E3C3C] disabled:opacity-30"
                                    disabled={projIndex === 0}
                                    onClick={() => {
                                      const updated = [...cv.experiences];
                                      const arr = updated[expIndex].projects;
                                      [arr[projIndex - 1], arr[projIndex]] = [arr[projIndex], arr[projIndex - 1]];
                                      setCV({ ...cv, experiences: updated });
                                    }}
                                    data-testid={`move-project-up-${expIndex}-${projIndex}`}
                                    title={language === 'pt' ? 'Mover para cima' : 'Move up'}
                                  >
                                    <ArrowUp className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-[#64748b] hover:text-[#1E3C3C] disabled:opacity-30"
                                    disabled={projIndex === (exp.projects?.length ?? 0) - 1}
                                    onClick={() => {
                                      const updated = [...cv.experiences];
                                      const arr = updated[expIndex].projects;
                                      [arr[projIndex], arr[projIndex + 1]] = [arr[projIndex + 1], arr[projIndex]];
                                      setCV({ ...cv, experiences: updated });
                                    }}
                                    data-testid={`move-project-down-${expIndex}-${projIndex}`}
                                    title={language === 'pt' ? 'Mover para baixo' : 'Move down'}
                                  >
                                    <ArrowDown className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-red-400 hover:text-red-600"
                                    onClick={() => {
                                      const updated = [...cv.experiences];
                                      updated[expIndex].projects = updated[expIndex].projects.filter((_, i) => i !== projIndex);
                                      setCV({ ...cv, experiences: updated });
                                    }}
                                    data-testid={`remove-project-${expIndex}-${projIndex}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                                <Input
                                  placeholder={isFunction
                                    ? (language === 'pt' ? 'Nome da função (ex: Senior Developer)' : 'Function name (e.g., Senior Developer)')
                                    : (language === 'pt' ? 'Nome do projeto' : 'Project name')}
                                  value={language === 'pt' ? (p.name_pt || '') : (p.name_en || '')}
                                  onChange={(e) => {
                                    const updated = [...cv.experiences];
                                    if (language === 'pt') updated[expIndex].projects[projIndex].name_pt = e.target.value;
                                    else updated[expIndex].projects[projIndex].name_en = e.target.value;
                                    setCV({ ...cv, experiences: updated });
                                  }}
                                  data-testid={`project-name-${expIndex}-${projIndex}`}
                                />
                                <div />{/* empty cell to keep the grid layout balanced */}
                                <Input
                                  type="month"
                                  placeholder="YYYY-MM"
                                  value={p.start_date || ''}
                                  onChange={(e) => {
                                    const updated = [...cv.experiences];
                                    updated[expIndex].projects[projIndex].start_date = e.target.value;
                                    setCV({ ...cv, experiences: updated });
                                  }}
                                  data-testid={`project-start-${expIndex}-${projIndex}`}
                                />
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="month"
                                    placeholder="YYYY-MM"
                                    value={p.end_date || ''}
                                    disabled={!!p.current}
                                    onChange={(e) => {
                                      const updated = [...cv.experiences];
                                      updated[expIndex].projects[projIndex].end_date = e.target.value;
                                      setCV({ ...cv, experiences: updated });
                                    }}
                                    data-testid={`project-end-${expIndex}-${projIndex}`}
                                  />
                                  <label className="flex items-center gap-1 text-xs text-[#64748b] whitespace-nowrap">
                                    <Checkbox
                                      checked={!!p.current}
                                      onCheckedChange={(v) => {
                                        const updated = [...cv.experiences];
                                        updated[expIndex].projects[projIndex].current = !!v;
                                        if (v) updated[expIndex].projects[projIndex].end_date = '';
                                        setCV({ ...cv, experiences: updated });
                                      }}
                                      data-testid={`project-current-${expIndex}-${projIndex}`}
                                    />
                                    {language === 'pt' ? 'Atual' : 'Current'}
                                  </label>
                                </div>
                              </div>
                              <div className="flex items-center justify-between mb-1">
                                <Label className="text-xs text-[#64748b]">
                                  {language === 'pt' ? 'Descrição (Bullet Points)' : 'Description (Bullet Points)'}
                                </Label>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs text-[#1E3C3C] hover:bg-[#1E3C3C]/10 h-7"
                                  data-testid={`suggest-project-bullets-btn-${expIndex}-${projIndex}`}
                                  disabled={suggestingProjectBulletsKey === `${expIndex}-${projIndex}`}
                                  onClick={() => handleSuggestProjectBullets(expIndex, projIndex)}
                                  title={language === 'pt' ? 'IA sugere responsabilidades para este projeto' : 'AI suggests responsibilities for this project'}
                                >
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  {suggestingProjectBulletsKey === `${expIndex}-${projIndex}`
                                    ? (language === 'pt' ? 'A gerar…' : 'Generating…')
                                    : (language === 'pt' ? 'IA: mais responsabilidades' : 'AI: more responsibilities')}
                                </Button>
                              </div>
                              <Textarea
                                placeholder={language === 'pt' ? 'Descrição (um bullet por linha)' : 'Description (one bullet per line)'}
                                value={(language === 'pt' ? p.description_bullets_pt : p.description_bullets_en)?.join('\n') || ''}
                                onChange={(e) => {
                                  const updated = [...cv.experiences];
                                  const bullets = e.target.value.split('\n');
                                  if (language === 'pt') {
                                    updated[expIndex].projects[projIndex].description_bullets_pt = bullets;
                                  } else {
                                    updated[expIndex].projects[projIndex].description_bullets_en = bullets;
                                  }
                                  setCV({ ...cv, experiences: updated });
                                }}
                                className="min-h-[80px] text-sm mb-2"
                                data-testid={`project-bullets-${expIndex}-${projIndex}`}
                              />
                              <div className="flex items-center justify-between mb-1">
                                <Label className="text-xs text-[#64748b]">
                                  {language === 'pt' ? 'Tecnologias específicas (separadas por vírgula)' : 'Specific technologies (comma-separated)'}
                                </Label>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs text-[#1E3C3C] hover:bg-[#1E3C3C]/10 h-7"
                                  data-testid={`suggest-project-tech-btn-${expIndex}-${projIndex}`}
                                  disabled={suggestingProjectTechKey === `${expIndex}-${projIndex}`}
                                  onClick={() => handleSuggestProjectTechnologies(expIndex, projIndex)}
                                  title={language === 'pt' ? 'IA sugere tecnologias para este projeto' : 'AI suggests technologies for this project'}
                                >
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  {suggestingProjectTechKey === `${expIndex}-${projIndex}`
                                    ? (language === 'pt' ? 'A gerar…' : 'Generating…')
                                    : (language === 'pt' ? 'IA: sugerir' : 'AI: suggest')}
                                </Button>
                              </div>
                              <Input
                                placeholder={language === 'pt' ? 'Ex: Spring Boot, Kubernetes, AWS' : 'Ex: Spring Boot, Kubernetes, AWS'}
                                value={(p.technologies || []).map((t) => t.name).filter(Boolean).join(', ')}
                                onChange={(e) => {
                                  const updated = [...cv.experiences];
                                  const names = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                                  updated[expIndex].projects[projIndex].technologies = names.map((n) => ({ name: n, level: 3 }));
                                  setCV({ ...cv, experiences: updated });
                                }}
                                data-testid={`project-tech-${expIndex}-${projIndex}`}
                              />
                            </div>
                            );
                          })}
                          {(!exp.projects || exp.projects.length === 0) && (
                            <p className="text-xs text-[#94a3b8] italic py-2">
                              {language === 'pt' ? 'Sem funções ou projetos adicionados.' : 'No functions or projects added.'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" onClick={addExperience} className="w-full border-dashed" data-testid="add-experience-btn">
                    <Plus className="w-4 h-4 mr-2" />{t('addExperience')}
                  </Button>
                </AccordionContent>
              </AccordionItem>
              )}

              {/* Education */}
              <AccordionItem value="education" className="form-section">
                <AccordionTrigger className="form-section-title hover:no-underline">
                  <GraduationCap className="w-5 h-5" />
                  {t('educationCertifications')}
                </AccordionTrigger>
                <AccordionContent className="pt-4 space-y-6">
                  {/* Projects section — only for RL (Recém-Licenciado) CVs */}
                  {cv.cv_type === 'rl' && (
                    <div className="p-4 bg-[#FFFBEB] border border-[#FFD700]/40 rounded-lg">
                      <Label className="text-sm font-semibold text-[#B8860B] mb-2 block">
                        {language === 'pt' ? 'Projetos' : 'Projects'}
                      </Label>
                      <p className="text-xs text-[#64748b] mb-2">
                        {language === 'pt'
                          ? 'Descreva projetos académicos, pessoais, hackathons, etc. Esta secção só aparece em CVs de Recém-Licenciado.'
                          : 'Describe academic, personal, hackathon projects, etc. This section only appears on Recent Graduate CVs.'}
                      </p>
                      <AutoGrowTextarea
                        value={cv.rl_projects || ''}
                        onChange={(e) => setCV({ ...cv, rl_projects: e.target.value })}
                        placeholder={language === 'pt'
                          ? 'Ex: Projeto final de mestrado sobre visão computacional aplicada a…\n\nApp mobile de gestão de tarefas desenvolvida em React Native durante hackathon…'
                          : 'Ex: Final master project on computer vision applied to…\n\nMobile task-management app built with React Native during a hackathon…'}
                        rows={8}
                        className="w-full min-h-[200px] text-sm"
                        data-testid="rl-projects-input"
                      />
                    </div>
                  )}
                  <div>
                    <h4 className="font-medium text-[#0f172a] mb-3">{language === 'pt' ? 'Formação' : 'Education'}</h4>
                    {cv.education.map((edu, index) => (
                      <div key={`edu-${index}`} className="mb-4 p-4 bg-[#f8fafc] rounded-lg">
                        <div className="flex justify-end mb-2">
                          <Button variant="ghost" size="icon" onClick={() => removeEducation(index)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                        <div className="space-y-3">
                          <Input placeholder={t('institution')} value={edu.institution} onChange={(e) => updateEducation(index, 'institution', e.target.value)} />
                          <Input placeholder={t('degree')} value={language === 'pt' ? edu.degree_pt : edu.degree_en} onChange={(e) => updateEducation(index, language === 'pt' ? 'degree_pt' : 'degree_en', e.target.value)} />
                          <Input placeholder={t('fieldOfStudy')} value={language === 'pt' ? edu.field_pt : edu.field_en} onChange={(e) => updateEducation(index, language === 'pt' ? 'field_pt' : 'field_en', e.target.value)} />
                          <div className="grid grid-cols-2 gap-3">
                            <Input placeholder={language === 'pt' ? 'Ano Início' : 'Start Year'} value={edu.start_year} onChange={(e) => updateEducation(index, 'start_year', e.target.value)} />
                            <Input placeholder={language === 'pt' ? 'Ano Fim' : 'End Year'} value={edu.end_year} onChange={(e) => updateEducation(index, 'end_year', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" onClick={addEducation} className="w-full border-dashed"><Plus className="w-4 h-4 mr-2" />{t('addEducation')}</Button>
                  </div>
                  <div>
                    <h4 className="font-medium text-[#0f172a] mb-3">{language === 'pt' ? 'Certificações' : 'Certifications'}</h4>
                    {cv.certifications.map((cert, index) => (
                      <div key={`cert-${index}`} className="mb-4 p-4 bg-[#f8fafc] rounded-lg">
                        <div className="flex justify-end mb-2">
                          <Button variant="ghost" size="icon" onClick={() => removeCertification(index)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                        <div className="space-y-3">
                          <Input placeholder={t('certificationName')} value={cert.name} onChange={(e) => updateCertification(index, 'name', e.target.value)} />
                          <div className="grid grid-cols-2 gap-3">
                            <Input placeholder={t('issuer')} value={cert.issuer} onChange={(e) => updateCertification(index, 'issuer', e.target.value)} />
                            <Input placeholder={t('year')} value={cert.year} onChange={(e) => updateCertification(index, 'year', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" onClick={addCertification} className="w-full border-dashed"><Plus className="w-4 h-4 mr-2" />{t('addCertification')}</Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Preview Section - Matching Export Layout */}
          <div className="cv-preview-section" data-testid="cv-preview">
            <Card className="cv-paper">
              {/* Header with Logo and Client Name */}
              <CardHeader className="text-center pb-4">
                {cv.header_config?.logo_base64 && (
                  <div className="mb-4 flex justify-center">
                    <img src={cv.header_config.logo_base64} alt="Logo" className="h-16 object-contain" />
                  </div>
                )}
                {cv.client_name && (
                  <p className="text-xs font-medium tracking-wider text-[#64748b] uppercase mb-4">
                    {cv.client_name}
                  </p>
                )}
                <CardTitle 
                  className="text-2xl font-semibold" 
                  style={{ color: cv.header_config?.primary_color || '#1E3C3C' }}
                >
                  {cv.name || 'Nome/ID'}
                </CardTitle>
              </CardHeader>

              {/* Experience and Availability - Centered with separator line */}
              <div className="border-t border-[#e2e8f0]">
                <div className="py-6 text-center">
                  <p className="text-[#374151] text-base">
                    <span className="font-normal">{t('yearsExperience')}:</span>{' '}
                    <span className="font-medium">
                      {cv.experience_override || (
                        <>
                          {cv.years_experience || 0} {t('years')}
                          {cv.months_experience > 0 && (
                            <> {language === 'pt' ? 'e' : 'and'} {cv.months_experience} {language === 'pt' ? (cv.months_experience === 1 ? 'mês' : 'meses') : (cv.months_experience === 1 ? 'month' : 'months')}</>
                          )}
                        </>
                      )}
                    </span>
                    <span className="mx-3 text-[#d1d5db]">|</span>
                    <span className="font-normal">{t('availability')}:</span>{' '}
                    <span className="font-medium">{language === 'pt' ? cv.availability_pt : cv.availability_en}</span>
                  </p>
                </div>
              </div>

              {/* Additional Content (collapsed by default in preview) */}
              <CardContent className="pt-4 space-y-4 border-t border-[#e2e8f0]">
                {/* Languages */}
                {cv.languages.length > 0 && (
                  <div className="text-center">
                    <p className="text-sm text-[#64748b]">
                      {cv.languages.map(l => `${language === 'pt' ? l.name_pt : l.name_en} (${l.level})`).join(' • ')}
                    </p>
                  </div>
                )}

                {/* Main Competencies - Horizontal display */}
                {cv.main_competencies.length > 0 && (
                  <div className="text-center">
                    <h3 className="font-semibold text-sm mb-2" style={{ color: cv.header_config?.primary_color || '#1E3C3C' }}>
                      {t('mainCompetencies')}
                    </h3>
                    <p className="text-sm text-[#374151]">
                      {cv.main_competencies.map(comp => language === 'pt' ? comp.name_pt : comp.name_en).join(' • ')}
                    </p>
                  </div>
                )}

                {/* Summary */}
                {(cv.summary_pt || cv.summary_en) && (
                  <div>
                    <h3 className="font-semibold text-sm mb-2" style={{ color: cv.header_config?.primary_color || '#1E3C3C' }}>
                      {t('professionalSummary')}
                    </h3>
                    <p className="text-sm text-[#64748b] line-clamp-3">
                      {language === 'pt' ? cv.summary_pt : cv.summary_en}
                    </p>
                  </div>
                )}

                {/* Score Evolutt preview */}
                {cv.score_evolutt !== null && cv.score_evolutt !== undefined && (
                  <div className="text-center">
                    <div className="p-3 rounded-lg bg-white border border-[#1E3C3C] inline-block">
                      <p className="text-xs text-[#64748b]">Score Evolutt</p>
                      <p className="text-lg font-semibold text-[#1E3C3C]">
                        {cv.score_evolutt}%
                      </p>
                    </div>
                  </div>
                )}

                {/* Experience count */}
                {cv.experiences.length > 0 && (
                  <div className="text-center pt-2">
                    <p className="text-xs text-[#64748b]">
                      {cv.experiences.length} {language === 'pt' ? 'experiência(s) profissional(is)' : 'professional experience(s)'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Action Buttons */}
            <div className="mt-6 space-y-4">
              <Button onClick={handleSave} disabled={saving} className="w-full btn-primary py-5" data-testid="save-cv-btn">
                {saving ? <div className="loading-spinner w-5 h-5"></div> : <><Save className="w-4 h-4 mr-2" />{t('save')}</>}
              </Button>
              {autoSaveStatus !== 'idle' && (
                <div
                  className={`text-xs text-center mt-2 ${autoSaveStatus === 'error' ? 'text-red-600' : 'text-[#64748b]'}`}
                  data-testid="autosave-status"
                >
                  {autoSaveStatus === 'saving' && (language === 'pt' ? '● A guardar…' : '● Saving…')}
                  {autoSaveStatus === 'saved' && (language === 'pt' ? '✓ Guardado automaticamente' : '✓ Auto-saved')}
                  {autoSaveStatus === 'error' && (language === 'pt' ? '! Erro no auto-save (clique Guardar)' : '! Auto-save error (click Save)')}
                </div>
              )}
              {cvId && (
                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={handlePreview} disabled={previewing} variant="outline" className="border-[#1E3C3C] text-[#1E3C3C]" data-testid="preview-cv-btn">
                    {previewing ? <div className="loading-spinner w-4 h-4"></div> : <><Eye className="w-4 h-4 mr-2" />Preview</>}
                  </Button>
                  <Button onClick={handleExportWordClick} variant="outline" className="border-[#FFD700] text-[#1a202c] bg-[#FFD700] hover:bg-[#E6C200]" data-testid="export-word-btn">
                    <Download className="w-4 h-4 mr-2" />{t('exportWord')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Import CV Dialog - PDF/Word Upload */}
      <ImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        language={language}
        t={t}
        importFile={importFile}
        setImportFile={setImportFile}
        importing={importing}
        handleImportCV={handleImportCV}
      />

      {/* Manual Import CV Dialog - Rich Text Editor */}
      <ManualImportDialog
        open={showManualImportDialog}
        onOpenChange={setShowManualImportDialog}
        language={language}
        t={t}
        manualImportText={manualImportText}
        setManualImportText={setManualImportText}
        manualImporting={manualImporting}
        handleManualImportCV={handleManualImportCV}
      />

      {/* Bulk Add Competencies Dialog */}
      <BulkCompetenciesDialog
        open={showBulkCompDialog}
        onOpenChange={setShowBulkCompDialog}
        language={language}
        t={t}
        filteredCompetencies={filteredCompetencies}
        cv={cv}
        selectedBulkComps={selectedBulkComps}
        setSelectedBulkComps={setSelectedBulkComps}
        toggleBulkCompetency={toggleBulkCompetency}
        handleAddBulkCompetencies={handleAddBulkCompetencies}
      />

      {/* PDF Preview Dialog - inline iframe */}
      <PreviewDialog
        open={showPreviewDialog}
        onOpenChange={(open) => {
          setShowPreviewDialog(open);
          if (!open && previewUrl) {
            window.URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
        }}
        language={language}
        previewUrl={previewUrl}
      />

      {/* Translation Prompt Dialog — shown after Save */}
      <TranslateCVDialog
        open={showTranslateDialog}
        onOpenChange={(open) => { setShowTranslateDialog(open); if (!open) setTranslateLangs([]); }}
        language={language}
        t={t}
        sourceLang={cv.cv_language || language || 'pt'}
        selectedLangs={translateLangs}
        setSelectedLangs={setTranslateLangs}
        translating={translating}
        handleTranslate={handleTranslate}
      />

      {/* Export Word Language Prompt Dialog */}
      <ExportLangDialog
        open={showExportLangDialog}
        onOpenChange={(open) => { if (!exporting) setShowExportLangDialog(open); }}
        language={language}
        defaultLang={cv.cv_language || language || 'pt'}
        exporting={exporting}
        onConfirm={(lang) => handleExport(lang)}
      />

      {/* AI Summary Variation Picker — user picks 1 of 3 wordings */}
      <Dialog open={showSummaryPicker} onOpenChange={setShowSummaryPicker}>
        <DialogContent className="max-w-2xl" data-testid="summary-picker-dialog">
          <DialogHeader>
            <DialogTitle>
              {language === 'pt' ? 'Escolha o resumo' : 'Pick a summary'}
            </DialogTitle>
            <DialogDescription>
              {language === 'pt'
                ? 'A IA gerou 3 alternativas no estilo escolhido. Clique naquela que prefere para aplicar ao CV.'
                : 'The AI generated 3 alternatives in the chosen style. Click the one you prefer to apply it.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {generatingSummary && summaryVariations.length === 0 ? (
              // Loading skeleton — shown when "Gerar 3 novas" is regenerating
              // while the dialog is still open. Keeps context visible instead
              // of closing and reopening the dialog.
              <>
                {[0, 1, 2].map((i) => (
                  <div key={i} className="p-4 rounded-lg border border-[#e2e8f0] animate-pulse" data-testid={`summary-variation-skeleton-${i}`}>
                    <div className="h-3 w-16 bg-[#e2e8f0] rounded mb-3" />
                    <div className="h-3 w-full bg-[#e2e8f0] rounded mb-2" />
                    <div className="h-3 w-11/12 bg-[#e2e8f0] rounded mb-2" />
                    <div className="h-3 w-4/5 bg-[#e2e8f0] rounded" />
                  </div>
                ))}
              </>
            ) : (
              summaryVariations.map((text, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => applySummaryVariation(text)}
                  className="w-full text-left p-4 rounded-lg border border-[#e2e8f0] hover:border-[#1E3C3C] hover:bg-[#1E3C3C]/5 transition-colors"
                  data-testid={`summary-variation-${idx}`}
                >
                  <div className="text-xs font-semibold text-[#1E3C3C] mb-2 uppercase tracking-wide">
                    {language === 'pt' ? `Opção ${idx + 1}` : `Option ${idx + 1}`}
                  </div>
                  <div className="text-sm text-[#0f172a] whitespace-pre-wrap leading-relaxed">{text}</div>
                </button>
              ))
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setSummaryVariations([]); handleGenerateSummary(); }}
              disabled={generatingSummary}
              data-testid="summary-regenerate-btn"
            >
              {generatingSummary ? <div className="loading-spinner w-4 h-4 mr-2"></div> : <Sparkles className="w-4 h-4 mr-2" />}
              {generatingSummary
                ? (language === 'pt' ? 'A gerar...' : 'Generating...')
                : (language === 'pt' ? 'Gerar 3 novas' : 'Generate 3 new')}
            </Button>
            <Button variant="ghost" onClick={() => setShowSummaryPicker(false)} data-testid="summary-picker-cancel">
              {language === 'pt' ? 'Cancelar' : 'Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
